<template>
  <f7-page name="centro-cobros" @page:init="onPageInit">
    <f7-navbar>
      <f7-nav-left><f7-link back icon-f7="arrow_left"></f7-link></f7-nav-left>
      <f7-nav-title>Centro de Cobros</f7-nav-title>
      <f7-nav-right>
        <!-- #188 · la bandeja de asignación del gestor de cobro. La puerta está AQUÍ y no en el
             Cuadro de mandos porque es la misma cartera vista de otra manera: esta pantalla enseña
             el agujero («cartera con gestor») y la bandeja es donde se tapa. Se enseña a todo el que
             entra en el Centro de Cobros, incluido quien solo puede leerla: el criterio 6 dice que
             un gestor de cobro tiene que poder ver qué hay sin dueño en su área para pedirlo. Quién
             puede asignar lo decide el servidor allí. -->
        <f7-link href="/dashboard/cobros/asignacion/" icon-f7="person_badge_plus"
          style="color:#c8d3e5"></f7-link>
        <!-- #190 · «Mi trabajo de hoy». El enlace se dibuja SI Y SOLO SI el servidor dice que esa
             persona entra (ver `puedeEntrarTrabajoHoy`): un botón que lleva a un 403 es peor que no
             tener botón, porque hace creer que te han quitado permisos (dirección, 05/10/2026). Y
             quién entra no se puede deducir aquí: «gestor de cobro» no es un rol, es quien figura
             como PGC en alguna factura (D-COB-3). -->
        <f7-link href="/dashboard/cobros/mi-trabajo/" icon-f7="checkmark_square" v-if="puedeEntrarTrabajoHoy"
          style="color:#c8d3e5"></f7-link>
        <!-- #202 · recarga TODO lo que la pantalla está enseñando, cartera y cuadro de
             comportamiento. Y por eso no es `cargar` a secas (revisión CODEX, ronda 2): el cuadro
             tiene su propia lectura y se queda montado, así que con solo `cargar` este botón no lo
             tocaba —si su petición había fallado, el aviso del cuadro remitía aquí y aquí no pasaba
             nada—. Los filtros de abajo siguen llamando a `cargar` y solo a `cargar`: lo que piden
             es otra cartera, no otro cuadro. -->
        <f7-link @click="recargarTodo" icon-f7="arrow_2_circlepath" style="color:#c8d3e5"></f7-link>
      </f7-nav-right>
    </f7-navbar>
    <!--
      #16 · se lee en directo de la consulta de Facturas en la propia petición, sin caché. Y solo
      cuando hay cifras en pantalla: un «Datos en vivo» encima de un aviso de que no hay cifras
      contradice al propio aviso.
    -->
    <div class="sello-datos-bar" v-if="hayCifras"><sello-datos estado="vivo" /></div>
    <f7-page-content style="padding-top:0">

      <!-- #749 · el preloader a pantalla entera es SOLO de la primera carga, la única que no tiene
           nada que enseñar todavía. Una recarga con cifras delante no pasa por aquí: avisa en la
           cabecera del bloque que se actualiza y deja la pantalla donde está (ver `cargar`). -->
      <f7-block v-if="cargando" class="text-align-center"><f7-preloader></f7-preloader></f7-block>

      <f7-block v-if="error" class="aviso-error">{{ error }}</f7-block>

      <!-- #749 · `v-if="dto"`, sin el `&& !cargando` que llevaba.
           Ese `!cargando` era el fallo que dirección señaló el 20/10/2026: cada vez que se ponía o
           se quitaba un filtro, la recarga apagaba la condición, Vue desmontaba las cifras, los
           filtros y la tabla, y al llegar la respuesta se montaba todo otra vez con el scroll
           arriba. La pantalla en blanco y la vuelta al principio eran lo mismo: un `v-if` que la
           propia recarga apaga. Es la misma causa que lo de «Últimos 12 meses» del cuadro de
           comportamiento por forma de pago, que se corrigió en la #201. -->
      <template v-if="dto">

        <!-- D-COB-5 · qué parte de la cartera se está viendo. Lo dice el servidor, que es quien
             aplica el recorte: si lo escribiera la pantalla, podría decir «ves todo» con una
             consulta recortada. -->
        <div class="linea-alcance">
          <f7-icon f7="person_crop_circle" class="linea-alcance-icono"></f7-icon>
          <span>{{ dto.alcance }}</span>
        </div>

        <!-- #193 · COB-7 · la entrada al informe de abonos abiertos.
             SOLO si esa persona puede entrar: la respuesta viene como `prop` desde la ruta, resuelta
             con el `Usuario.me()` de esta carga (ver `routes.js`). Un enlace que lleva a un 403 es
             peor que no tener enlace, porque hace creer que le han quitado permisos; y como el
             permiso aquí no recorta sino que cierra, no hay nada que enseñarle en su lugar: quien no
             puede entrar no tiene una parte de este informe, no tiene informe.
             Lleva su rótulo escrito y no solo un icono en la barra: desde fuera, «abonos abiertos»
             no se adivina de un dibujo. -->
        <div class="entrada-informe" v-if="puedeVerInformeAbonos">
          <f7-link href="/dashboard/cobros/abonos/" class="entrada-informe-enlace">
            <f7-icon f7="doc_text" class="entrada-informe-icono"></f7-icon>
            <span class="entrada-informe-texto">
              <strong>Abonos abiertos</strong>
              <small>Lista de trabajo de Administración: los abonos sin casar, los que llevan más de
                un año y con qué factura casaría cada uno. No cambia nada.</small>
            </span>
            <f7-icon f7="chevron_right" class="entrada-informe-flecha"></f7-icon>
          </f7-link>
        </div>

        <!-- CRITERIO 10 · «saldo pendiente» no es «vencido», en texto y a la vista. El texto lo
             manda la API porque es la decisión D-COB-2 sobre el dato, no una frase de pantalla. -->
        <div class="nota-dato">
          <f7-icon f7="info_circle" class="nota-dato-icono"></f7-icon>
          <span>{{ dto.notaSaldoPendiente }}</span>
        </div>

        <!-- D-COB-12 · gestiones que no se han podido atribuir a ninguna factura. Va ARRIBA y no
             al pie de la tabla porque afecta a la tarjeta «cartera con gestor» y a la columna «nº
             de gestiones», y quien mire una cifra corta tiene que encontrar aquí por qué. El texto
             lo manda la API: es la decisión de dirección sobre el dato, no una frase de pantalla.
             Y solo aparece si hay alguna: una pantalla que avisa de que no pasa nada enseña a no
             leer los avisos. -->
        <div class="nota-dato nota-dato-alerta" v-if="dto.notaGestionesNoEmparejables">
          <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
          <span>{{ dto.notaGestionesNoEmparejables }}</span>
        </div>

        <!-- #190 · LA PUERTA A «MI TRABAJO DE HOY». Esta pantalla MIDE; la de la lista diaria es la
             que ACTÚA, así que la puerta va arriba y no al pie. Solo para quien entra: lo decide el
             servidor con la misma pregunta que usa la barrera de esa ruta, para que no puedan decir
             cosas distintas. -->
        <div class="bloque bloque-trabajo-hoy" v-if="puedeEntrarTrabajoHoy">
          <p class="bloque-titulo">Mi trabajo de hoy</p>
          <p class="trabajo-hoy-nota">
            La lista diaria de gestión de cobro: las diez facturas vencidas de más prioridad, con el
            motivo por el que están ahí, el contacto del cliente y el botón para crear la gestión.
          </p>
          <f7-button fill small class="boton-trabajo-hoy" href="/dashboard/cobros/mi-trabajo/">
            Abrir mi trabajo de hoy
          </f7-button>
        </div>

        <!-- CINCO TARJETAS DE CABECERA · #749 · plegables, y son el único grupo de la pantalla que
             NO tenía cabecera: dirección pidió que se pudieran plegar, así que se le añade una
             línea de rótulo encima y nada más. Sin caja (`:caja="false"`), porque las tarjetas ya
             tienen la suya y meterlas dentro de otra las movería de sitio. -->
        <bloque-plegable class="titulares-plegable" titulo="Las cifras clave"
          :abierto="plegado.titulares" :resumen="resumenTitulares" :refrescando="!!refrescando"
          :caja="false" @alternar="alternarBloque('titulares')">
        <div class="titulares">
          <div class="titular">
            <p class="titular-rotulo">Pendiente total</p>
            <p class="titular-cifra">{{ formatEuros(tarjetas.pendienteTotal) }} €</p>
            <p class="titular-nota">
              {{ tarjetas.documentos }} documentos
              ({{ tarjetas.facturas }} facturas y {{ tarjetas.abonos }} abonos)
            </p>
          </div>
          <div class="titular">
            <p class="titular-rotulo">Vencido</p>
            <!-- La cifra grande es la de los abonos incluidos: el MISMO conjunto que «Pendiente
                 total», que es lo que pidió dirección el 04/10/2026 al ver 747.162 € de vencido
                 encima de 735.418 € de pendiente. Las dos cuentas siguen valiendo (criterio 7) y
                 las dos siguen aquí; lo que cambia es cuál va grande y cuál va debajo. -->
            <p class="titular-cifra titular-cifra-alerta">
              {{ formatEuros(tarjetas.vencidoConAbonos) }} €
            </p>
            <p class="titular-nota">
              {{ tarjetas.documentosVencidoConAbonos }} de {{ tarjetas.documentos }} documentos
              (facturas y abonos)
            </p>
            <!-- El número descartado, como segundo dato y rotulado con lo que lo distingue: sin los
                 abonos. Sin ese rótulo es la cifra de al lado sin que se sepa por qué difiere. -->
            <p class="titular-nota titular-nota-segundo">
              Sin contar los abonos: {{ formatEuros(tarjetas.vencidoSoloFacturas) }} € en
              {{ tarjetas.documentosVencidoSoloFacturas }} facturas
            </p>
            <p class="titular-nota titular-nota-explica" v-if="tarjetas.notaVencido">
              {{ tarjetas.notaVencido }}
            </p>
            <!-- Cuando el vencido sí se pasa del total midiendo lo mismo, es porque la parte NO
                 vencida suma en negativo, y son DOS las partes que pueden hacerlo: la que está en
                 plazo y la que no tiene fecha de vencimiento. Cuál de las dos es lo dice la API,
                 que es quien tiene las tres partes del conjunto, y el aviso solo sale cuando pasa:
                 no se escribe aquí la causa, porque aquí no se ha medido. -->
            <p class="titular-nota titular-nota-aviso" v-if="tarjetas.notaVencidoSobreElTotal">
              {{ tarjetas.notaVencidoSobreElTotal }}
            </p>
            <p class="titular-nota titular-nota-aviso" v-if="tarjetas.documentosSinVencimiento">
              {{ tarjetas.documentosSinVencimiento }} sin fecha de vencimiento
              ({{ formatEuros(tarjetas.importeSinVencimiento) }} €): no se puede decir si están
              vencidos
            </p>
          </div>
          <div class="titular">
            <p class="titular-rotulo">Más de 2 años</p>
            <p class="titular-cifra titular-cifra-alerta">
              {{ formatEuros(tarjetas.masDeDosAnios) }} €
            </p>
            <p class="titular-nota">{{ tarjetas.documentosMasDeDosAnios }} documentos</p>
          </div>
          <div class="titular">
            <p class="titular-rotulo">Edad media por euro</p>
            <p class="titular-cifra">{{ formatDias(tarjetas.edadMediaPonderadaDias) }}</p>
            <!-- Esta tarjeta es la otra que NO mide el conjunto entero —los abonos no pesan en una
                 media ponderada por euro—, así que dice sobre cuántos está hecha. Mismo criterio
                 que el segundo dato del vencido: el conjunto de una cifra de cabecera se lee en la
                 propia tarjeta, no en la letra pequeña. -->
            <p class="titular-nota titular-nota-segundo" v-if="hayRecuentoEdadMedia">
              Calculada sobre {{ tarjetas.documentosEdadMedia }} de
              {{ tarjetas.documentos }} documentos
            </p>
            <p class="titular-nota titular-nota-explica">{{ tarjetas.notaEdadMedia }}</p>
          </div>
          <div class="titular" :class="{'titular-aviso': tarjetas.avisoGestor}">
            <p class="titular-rotulo">Cartera con gestor</p>
            <p class="titular-cifra">{{ formatPorcentaje(tarjetas.porcentajeConGestor) }}</p>
            <p class="titular-nota">
              {{ tarjetas.documentosConGestor }} de {{ tarjetas.documentos }}
            </p>
            <!-- #188 · hasta ahora esta nota decía que asignar el gestor «es otra tarea». Ya
                 existe: es la bandeja, y desde aquí se entra. Una tarjeta que señala un agujero y
                 no dice dónde se tapa obliga a buscarlo por el menú. -->
            <p class="titular-nota titular-nota-aviso" v-if="tarjetas.avisoGestor">
              Una cartera sin dueño no la reclama nadie.
              <f7-link href="/dashboard/cobros/asignacion/" class="enlace-bandeja">
                Asignar el gestor de cobro
              </f7-link>
            </p>
          </div>
        </div>
        </bloque-plegable>

        <!-- D-COB-11 · LAS DOS CIFRAS JUNTAS, con la línea que explica por qué difieren. -->
        <bloque-plegable class="dos-cifras" titulo="Las dos cifras del pendiente"
          :abierto="plegado.dosCifras" :resumen="resumenDosCifras"
          :refrescando="refrescando === 'TODO'" @alternar="alternarBloque('dosCifras')">
          <div class="dos-cifras-fila">
            <div class="dos-cifras-celda">
              <p class="dos-cifras-rotulo">Cartera completa</p>
              <p class="dos-cifras-valor">{{ formatEuros(dosCifras.cartera) }} €</p>
              <p class="dos-cifras-origen">
                {{ dosCifras.documentosCartera }} documentos · {{ dosCifras.origenCartera }}
              </p>
            </div>
            <div class="dos-cifras-celda">
              <p class="dos-cifras-rotulo">Pendiente de cobro de FACTURACIÓN</p>
              <p class="dos-cifras-valor" v-if="dosCifras.pendienteFacturacion !== null && dosCifras.pendienteFacturacion !== undefined">
                {{ formatEuros(dosCifras.pendienteFacturacion) }} €
              </p>
              <p class="dos-cifras-valor dos-cifras-valor-falta" v-else>—</p>
              <p class="dos-cifras-origen" v-if="dosCifras.origenPendienteFacturacion">
                {{ dosCifras.origenPendienteFacturacion }}
              </p>
              <p class="dos-cifras-origen" v-if="dosCifras.faltaPendienteFacturacion">
                {{ dosCifras.faltaPendienteFacturacion }}
              </p>
            </div>
            <div class="dos-cifras-celda" v-if="dosCifras.diferencia !== null && dosCifras.diferencia !== undefined">
              <p class="dos-cifras-rotulo">Diferencia</p>
              <p class="dos-cifras-valor">{{ formatEuros(dosCifras.diferencia) }} €</p>
            </div>
          </div>
          <p class="dos-cifras-explicacion">{{ dosCifras.explicacion }}</p>
        </bloque-plegable>

        <!-- #200 · LA ESCALERA DE LA RECLAMACIÓN · dónde está parado el dinero y cuánto tarda en
             pasar al escalón siguiente. De solo lectura: aquí no se cambia el estado de nada.

             Va ANTES de los filtros y lo dice en su cabecera, porque no se recorta con ellos: la
             suma de sus filas es el total de documentos abiertos que esa persona ve (criterio 2),
             que es lo que permite cotejarla contra el listado de facturas pendientes del día sin
             tener que acordarse de qué filtros había puestos. -->
        <bloque-plegable v-if="escalera.escalones" titulo="La escalera de la reclamación"
          nota="· toda la cartera que ves, sin los filtros de abajo"
          :abierto="plegado.escalera" :resumen="resumenEscalera"
          :refrescando="refrescando === 'TODO'" @alternar="alternarBloque('escalera')">

          <!-- D-COB-8 · documentos de los que no consta cuándo entraron en el escalón en el que
               están: no tienen histórico, o su último apunte no trae fecha, o no es el del estado
               que tienen hoy. Las tres son el mismo hueco —no se sabe desde cuándo están ahí—, así
               que no cuentan en ninguna media. En ámbar y solo si hay alguno, mismo criterio que el
               aviso de las gestiones sin atribuir. -->
          <div class="nota-dato nota-dato-alerta" v-if="escalera.documentosSinFechaDeEntrada">
            <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
            <span>
              De {{ escalera.documentosSinFechaDeEntrada }} de los {{ escalera.documentos }}
              documentos no consta cuándo entraron en su escalón: no tienen histórico de estados de
              gestión, o su último apunte no trae fecha, o no es el del estado que tienen hoy. No
              cuentan en los días medios.
            </span>
          </div>

          <!-- D-COB-8 · cambios del histórico que no dicen de qué escalón salían: no se pueden
               atribuir a ninguna fila, así que no entran en ninguna media y se dicen aquí. Cero
               normalmente: hace falta un apunte con el estado en blanco o ilegible. -->
          <div class="nota-dato nota-dato-alerta" v-if="escalera.tramosSinEscalon">
            <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
            <span>
              Hay {{ escalera.tramosSinEscalon }} cambios de estado en el histórico que no dicen de
              qué escalón salían: no se pueden atribuir a ninguna fila y no cuentan en ningún
              promedio. El histórico de alguna factura está incompleto.
            </span>
          </div>

          <div class="tabla-scroll">
            <table class="tabla">
              <thead>
                <tr>
                  <th scope="col">Escalón</th>
                  <th scope="col" class="num">Documentos</th>
                  <th scope="col" class="num">Importe parado</th>
                  <th scope="col" class="num">Días medios al siguiente</th>
                  <th scope="col" class="num">Se recupera</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="escalon in escalones" :key="escalon.escalon === null ? 'sin-estado' : escalon.escalon"
                  :class="{'fila-sin-escalon': !escalon.delEnum}">
                  <td>
                    {{ escalon.etiqueta }}
                    <!-- La fila «sin estado» y la de un valor que no es del enum salen igual, para
                         que la suma siga dando el total, pero no son escalones y se marcan. -->
                    <span class="etiqueta-no-escalon" v-if="!escalon.delEnum">no es un escalón</span>
                    <span class="escalon-detalle" v-if="detalleEscalon(escalon)">
                      {{ detalleEscalon(escalon) }}
                    </span>
                  </td>
                  <td class="num">{{ escalon.documentos }}</td>
                  <td class="num">{{ formatEuros(escalon.importe) }}</td>
                  <!-- Un hueco es un guion, nunca un cero: cero diría que se pasa al escalón
                       siguiente el mismo día, y lo que pasa es que no se sabe (criterio 4). -->
                  <td class="num" :class="{'celda-falta': escalon.diasMedios === null || escalon.diasMedios === undefined}">
                    {{ formatDias(escalon.diasMedios) }}
                  </td>
                  <!-- Criterio 5 · a raya con el motivo escrito debajo de la tabla, no a cero. -->
                  <td class="num celda-falta">{{ formatEuros(escalon.recuperado) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <!-- El total lo manda el servidor y la pantalla NO lo suma: es la cifra con la que
                     se comprueba el criterio 2, y una pantalla que suma lo que quiere no comprueba
                     nada. -->
                <!-- El rótulo es la cabecera de ESTA fila (`scope="row"`); los totales son dato, no
                     cabecera de nada, así que van en `td`. -->
                <tr>
                  <th scope="row">Total de documentos abiertos</th>
                  <td class="num">{{ escalera.documentos }}</td>
                  <td class="num">{{ formatEuros(escalera.importeTotal) }}</td>
                  <td class="num"></td>
                  <td class="num"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Los cuatro textos los escribe la API: son las decisiones de dirección sobre el dato
               (D-COB-7, D-COB-8 y el criterio 6), no frases de pantalla. -->
          <p class="nota-dato nota-dato-pie">{{ escalera.notaSinGestionar }}</p>
          <p class="nota-dato nota-dato-pie">{{ escalera.notaDiasMedios }}</p>
          <p class="nota-dato nota-dato-pie">{{ escalera.notaRecuperado }}</p>
          <p class="nota-dato nota-dato-pie">{{ escalera.notaSuma }}</p>
        </bloque-plegable>

        <!-- #189 · LAS PERSONAS · quién responde de cada euro pendiente. Dos tablas de SOLO
             LECTURA y la fila de aviso: desde aquí no se asigna gestor —eso es la bandeja—, no se
             crean tareas y no se escribe nada.

             Va donde la escalera y por el mismo motivo: no se recorta con los filtros de abajo, así
             que los totales de las dos tablas son el pendiente total de la cartera que esa persona
             ve y se pueden cotejar contra el listado de facturas pendientes del día sin acordarse
             de qué filtros había puestos. -->
        <bloque-plegable v-if="personas.ventas" titulo="Las personas"
          nota="· toda la cartera que ves, sin los filtros de abajo"
          :abierto="plegado.personas" :resumen="resumenPersonas"
          :refrescando="refrescando === 'TODO'" @alternar="alternarBloque('personas')">

          <!-- D-COB-8 · si el desplegable de gestores de la bandeja no ofrece a nadie, la tabla de
               abajo sale con la fila de aviso sola y eso no se distingue de un fallo de carga. El
               texto lo manda la API, que es quien sabe si no hay empleados o si hay y ninguno está
               activo. -->
          <div class="nota-dato nota-dato-alerta" v-if="personas.faltaGestores">
            <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
            <span>{{ personas.faltaGestores }}</span>
          </div>

          <!-- a) POR GESTOR DE COBRO (PGC) · #749 · con su propio triángulo, que es lo que pidió
               dirección: «"Las personas" con el listado de empleados de ventas y el de gestores».
               Sin caja y como subtítulo: viven dentro del bloque de arriba y no se mueven de ahí. -->
          <bloque-plegable titulo="Por gestor de cobro" :abierto="plegado.personasGestores"
            :resumen="resumenGestores" :caja="false" sub
            @alternar="alternarBloque('personasGestores')">
          <div class="tabla-scroll">
            <table class="tabla">
              <thead>
                <tr>
                  <th scope="col">Gestor (PGC)</th>
                  <th scope="col" class="num">Cartera asignada</th>
                  <th scope="col" class="num">Vencido</th>
                  <th scope="col" class="num">Gestiones 7 días</th>
                  <th scope="col" class="num">Tiempo dedicado</th>
                  <th scope="col" class="num">Promesas vivas</th>
                  <th scope="col" class="num">Promesas incumplidas</th>
                  <th scope="col" class="num">Cobrado tras gestión (30 d)</th>
                  <th scope="col" class="num">
                    Con {{ personas.umbralCambiosDeGestor }} o más cambios de gestor
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- LA FILA DE AVISO, arriba de todo y destacada: es la fila de la que hay que
                     bajar, y hoy es el 100 % de la cartera. Va fuera del v-for para que ningún
                     orden la pueda mover de aquí. -->
                <tr class="fila-aviso" v-if="personas.sinAsignar">
                  <th scope="row">
                    SIN ASIGNAR
                    <span class="marca-aviso">nadie responde de este dinero</span>
                  </th>
                  <td class="num">{{ formatEuros(personas.sinAsignar.carteraAsignada) }} €</td>
                  <td class="num">{{ formatEuros(personas.sinAsignar.vencido) }} €</td>
                  <td class="num celda-falta">—</td>
                  <td class="num celda-falta">—</td>
                  <td class="num celda-falta">—</td>
                  <td class="num celda-falta">—</td>
                  <td class="num celda-falta">—</td>
                  <td class="num">{{ numero(personas.sinAsignar.documentosConDemasiadosCambios) }}</td>
                </tr>
                <tr v-for="(gestor, indice) in gestores" :key="'g-' + indice">
                  <th scope="row">
                    {{ gestor.nombre || gestor.username }}
                    <span class="gestor-username" v-if="gestor.nombre">{{ gestor.username }}</span>
                    <!-- Un gestor con cartera que ya no está en el desplegable de la bandeja:
                         normalmente un empleado que ya no está activo. Su fila sale igual —si no,
                         su cartera desaparecería de la tabla— y se marca, porque es cartera que
                         hay que volver a repartir. -->
                    <span class="marca-fuera" v-if="gestor.fueraDelDesplegable">
                      ya no está en el desplegable
                    </span>
                  </th>
                  <!-- Una raya NO es un cero (criterio 5): mientras no se le haya asignado nada, la
                       API manda nulo y aquí se pinta un guion. Un cero diría «tiene cartera y vale
                       cero euros», que es lo contrario de lo que pasa. -->
                  <td class="num" :class="{'celda-falta': gestor.carteraAsignada === null || gestor.carteraAsignada === undefined}">
                    {{ formatEurosConUnidad(gestor.carteraAsignada) }}
                  </td>
                  <td class="num" :class="{'celda-falta': gestor.vencido === null || gestor.vencido === undefined}">
                    {{ formatEurosConUnidad(gestor.vencido) }}
                  </td>
                  <td class="num celda-falta">—</td>
                  <td class="num celda-falta">—</td>
                  <td class="num celda-falta">—</td>
                  <td class="num celda-falta">—</td>
                  <td class="num celda-falta">—</td>
                  <td class="num" :class="{'celda-falta': gestor.documentosConDemasiadosCambios === null || gestor.documentosConDemasiadosCambios === undefined}">
                    {{ numero(gestor.documentosConDemasiadosCambios) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Los cuatro textos los escribe la API: son las decisiones de dirección sobre el dato
               (D-COB-8, criterio 5 y las columnas aplazadas a #191), no frases de pantalla. Y van
               AQUÍ, junto a la tabla cuyas columnas explican, no al principio de la pantalla. -->
          <p class="nota-dato nota-dato-pie">{{ personas.notaSinAsignar }}</p>
          <p class="nota-dato nota-dato-pie">{{ personas.notaRaya }}</p>
          <p class="nota-dato nota-dato-pie">{{ personas.faltaColumnasDeGestion }}</p>
          <p class="nota-dato nota-dato-pie">{{ personas.faltaCobradoTrasGestion }}</p>
          <p class="nota-dato nota-dato-pie">{{ personas.notaCambiosDeGestor }}</p>
          </bloque-plegable>

          <!-- b) POR EMPLEADO DE VENTAS (Empl. Contratación) -->
          <bloque-plegable class="bloque-subtitulo-separado" titulo="Por empleado de ventas"
            :abierto="plegado.personasVentas" :resumen="resumenVentas" :caja="false" sub
            @alternar="alternarBloque('personasVentas')">
          <div class="tabla-scroll">
            <table class="tabla">
              <thead>
                <tr>
                  <th scope="col">Empleado</th>
                  <th scope="col" class="num">Docs</th>
                  <th scope="col" class="num">Pendiente</th>
                  <th scope="col" class="num">Peso</th>
                  <th scope="col" class="num">Peso acumulado</th>
                  <th scope="col" class="num">Edad media por euro</th>
                  <th scope="col" class="num">Vencido</th>
                  <th scope="col" class="num">Sin ninguna gestión</th>
                  <th scope="col" class="num">Clientes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(fila, indice) in personas.ventas" :key="'v-' + indice"
                  :class="{'fila-sin-dato': fila.sinEmpleado}">
                  <th scope="row">
                    {{ fila.sinEmpleado ? 'Sin empleado de ventas' : fila.username }}
                    <span class="marca-fuera" v-if="fila.sinEmpleado">no es de nadie</span>
                  </th>
                  <td class="num">{{ fila.documentos }}</td>
                  <td class="num">{{ formatEuros(fila.pendiente) }} €</td>
                  <td class="num">{{ formatPorcentaje(fila.pesoPorcentaje) }}</td>
                  <!-- El acumulado es lo que convierte el criterio 2 en algo que se lee: en la
                       segunda fila ya pone cuánto pesan los dos primeros juntos. -->
                  <td class="num">{{ formatPorcentaje(fila.pesoAcumuladoPorcentaje) }}</td>
                  <td class="num" :class="{'num-alerta': fila.edadMediaPasaDelAnio}">
                    {{ formatDias(fila.edadMediaPonderadaDias) }}
                    <!-- Criterio 3 · la marca la decide el servidor con su umbral, no la pantalla. -->
                    <span class="marca-edad" v-if="fila.edadMediaPasaDelAnio">
                      pasa del año
                    </span>
                  </td>
                  <td class="num">
                    {{ formatEuros(fila.vencido) }} €
                    <span class="celda-nota" v-if="fila.documentosSinVencimiento">
                      {{ fila.documentosSinVencimiento }} sin fecha de vencimiento
                    </span>
                  </td>
                  <td class="num">
                    {{ formatEuros(fila.importeSinGestion) }} €
                    <span class="celda-nota">{{ fila.documentosSinGestion }} docs</span>
                  </td>
                  <td class="num">
                    {{ fila.clientes }}
                    <span class="celda-nota" v-if="fila.documentosSinCliente">
                      {{ fila.documentosSinCliente }} sin cliente
                    </span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <!-- El total lo manda el servidor y la pantalla NO lo suma: es la cifra con la que
                     se comprueba el criterio 4 contra el listado del día, y una pantalla que suma
                     lo que quiere no comprueba nada. Mismo criterio que el pie de la escalera. -->
                <tr>
                  <th scope="row">Total de la cartera que ves</th>
                  <td class="num">{{ personas.documentos }}</td>
                  <td class="num">{{ formatEuros(personas.importeTotal) }} €</td>
                  <!-- El pie no repite el 100 %: el peso acumulado de la última fila ya lo dice, y
                       escribirlo aquí sería la pantalla afirmando un porcentaje que no ha calculado
                       el servidor. -->
                  <td class="num"></td>
                  <td class="num"></td>
                  <td class="num"></td>
                  <td class="num"></td>
                  <td class="num"></td>
                  <td class="num"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p class="nota-dato nota-dato-pie">{{ personas.notaPeso }}</p>
          <p class="nota-dato nota-dato-pie">{{ personas.notaEdadMedia }}</p>
          <p class="nota-dato nota-dato-pie">{{ personas.notaSinGestion }}</p>
          </bloque-plegable>
        </bloque-plegable>

        <!-- #201 · LA CALIDAD DEL DATO · lo que no está relleno se dice, no se disimula
             (D-COB-8). De SOLO LECTURA: no corrige estados, no asigna gestor, no rellena plazos,
             no cierra documentos y no escribe nada.

             Va donde la escalera y las personas, y por el mismo motivo: no se recorta con los
             filtros de abajo. El denominador de los porcentajes es el total de documentos abiertos
             de la cartera que esa persona ve, que es lo que permite cotejar la tabla contra el
             listado de facturas pendientes del día sin acordarse de qué filtros había puestos. -->
        <bloque-plegable v-if="calidad.huecos" titulo="La calidad del dato"
          nota="· toda la cartera que ves, sin los filtros de abajo"
          :abierto="plegado.calidad" :resumen="resumenCalidad"
          :refrescando="refrescando === 'TODO'" @alternar="alternarBloque('calidad')">

          <!-- Que esto solo informa, lo primero y escrito por la API: es la decisión de dirección
               sobre el dato (criterio 8), no una frase de pantalla. -->
          <div class="nota-dato">
            <f7-icon f7="info_circle" class="nota-dato-icono"></f7-icon>
            <span>{{ calidad.notaSoloLectura }}</span>
          </div>

          <!-- a) ALERTA · «Cobrado total» y sigue abierta. NOMBRA las facturas, no las cuenta:
               una cifra sin la lista detrás no la arregla nadie (dirección, 04/10/2026).

               Y SALE SIEMPRE, también cuando no hay ninguna (dirección, 20/10/2026, al probar la
               pantalla). Antes se escondía a cero, con el criterio de que un aviso de que no pasa
               nada enseña a no leer los avisos; lo que eso rompe es que la línea ausente no
               distingue «se ha comprobado y no hay ninguna» de «no se ha comprobado», y este bloque
               está precisamente para decir lo que hay y lo que falta (D-COB-8).

               Lo que SÍ cambia a cero es la forma: no es una alerta, así que no lleva el triángulo
               —un aviso rojo que dice que todo está bien enseña a no leerlos igual— y no lleva la
               tabla, que sería una cabecera sola debajo de un «ninguna». Mismo criterio que la
               tabla de empresa extinta de abajo. -->
          <template v-if="cobradoTotal">
            <p class="bloque-subtitulo bloque-subtitulo-separado">
              {{ cobradoTotal.etiqueta }}
              <span class="bloque-nota" v-if="hayCobradoTotal">
                · {{ cobradoTotal.documentos }} documentos
                por {{ formatEuros(cobradoTotal.importe) }} €
              </span>
              <!-- «Ninguna» Y el cero, los dos: el cero es la cifra con la que se coteja contra el
                   listado del día y la palabra es lo que se lee de un vistazo. -->
              <span class="bloque-nota" v-else>
                · ninguna ({{ cobradoTotal.documentos }} documentos)
              </span>
            </p>
            <div class="nota-dato" :class="{'nota-dato-alerta': hayCobradoTotal}">
              <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"
                v-if="hayCobradoTotal"></f7-icon>
              <f7-icon f7="checkmark_circle" class="nota-dato-icono" v-else></f7-icon>
              <span>{{ cobradoTotal.texto }}</span>
            </div>
            <div class="tabla-scroll" v-if="hayCobradoTotal">
              <table class="tabla">
                <thead>
                  <tr>
                    <th scope="col">Cliente</th>
                    <th scope="col">Documento</th>
                    <th scope="col" class="num">Nº</th>
                    <th scope="col">Vencimiento</th>
                    <th scope="col" class="num">Saldo pendiente</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="doc in cobradoTotal.listado" :key="'ct-' + doc.id"
                    :class="{'fila-abono': doc.tipo === 'ABONO'}">
                    <td>{{ texto(doc.cliente) }}</td>
                    <td>
                      {{ texto(doc.codigo) }}
                      <span class="etiqueta-abono" v-if="doc.tipo === 'ABONO'">abono</span>
                    </td>
                    <td class="num">{{ numero(doc.numero) }}</td>
                    <td>{{ formatFecha(doc.fechaVencimiento) }}</td>
                    <!-- Un hueco de saldo es un guion, nunca un cero: el documento cuenta como
                         documento y no aporta euros, que es lo que dice la línea de abajo. -->
                    <td class="num" :class="{'celda-falta': doc.saldoPendiente === null || doc.saldoPendiente === undefined}">
                      {{ formatEurosConUnidad(doc.saldoPendiente) }}
                    </td>
                    <td>
                      <button type="button" class="boton-gestiones" @click="abrirGestiones(doc)">
                        Gestiones
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="nota-dato nota-dato-pie" v-if="cobradoTotal.documentosSinSaldo">
              {{ cobradoTotal.documentosSinSaldo }} de estos documentos han llegado sin «Saldo
              Vencido», así que cuentan como documento pero no aportan euros al importe de arriba.
            </p>
            <!-- El botón va AQUÍ, dentro del aviso que dice que faltan documentos, y no en la
                 tabla de huecos de abajo: esa tabla solo tiene las cinco filas de D-COB-8, así que
                 remitir a ella dejaría los documentos recortados sin ninguna forma de alcanzarlos
                 justo cuando se avisa de que existen. Y solo cuando el tope ha recortado algo: con
                 la lista entera arriba, el botón abriría la misma tabla otra vez. -->
            <p class="nota-dato nota-dato-pie" v-if="cobradoTotal.documentosOmitidos">
              Se nombran los {{ cobradoTotal.listado.length }} de mayor saldo;
              {{ cobradoTotal.documentosOmitidos }} no caben aquí. Las cifras de arriba cuentan
              todos.
              <button type="button" class="boton-gestiones"
                @click="abrirDocumentosDeLaCalidad(cobradoTotal)">
                Ver la lista completa
              </button>
            </p>
          </template>

          <!-- a) LÍNEA · empresa extinta. Sale SIEMPRE, también a cero (criterio 5): no es un
               aviso, es lo que explica por qué el pendiente gestionable puede ser menor que el
               total. Y los documentos siguen abiertos: no se dan de baja (D-COB-9). -->
          <p class="bloque-subtitulo bloque-subtitulo-separado">Lo que no es gestionable</p>
          <div class="dos-cifras-fila">
            <div class="dos-cifras-celda">
              <p class="dos-cifras-rotulo">Pendiente gestionable</p>
              <p class="dos-cifras-valor">{{ formatEuros(calidad.pendienteGestionable) }} €</p>
              <p class="dos-cifras-origen">
                {{ calidad.documentosGestionables }} documentos · es la cifra que hay que bajar
                reclamando
              </p>
            </div>
            <div class="dos-cifras-celda" v-if="extinta">
              <p class="dos-cifras-rotulo">{{ extinta.etiqueta }}</p>
              <!-- Cero documentos no tiene un importe de cero euros: la API manda nulo y aquí se
                   pinta un guion. Un «0 €» se leería como una medida y es que no hay nada medido. -->
              <p class="dos-cifras-valor" :class="{'dos-cifras-valor-falta': extinta.importe === null || extinta.importe === undefined}">
                {{ formatEurosConUnidad(extinta.importe) }}
              </p>
              <!-- «Siguen abiertos» y no «se siguen gestionando»: esta celda está debajo de «Lo
                   que no es gestionable» y su importe está FUERA del pendiente gestionable, así que
                   llamarlos gestionables aquí mandaba a reclamar lo que la cifra de al lado acaba de
                   apartar. Lo que D-COB-9 garantiza —que no se dan de baja ni se provisionan— lo
                   dice entero el texto de la API de abajo, con esas palabras. -->
              <p class="dos-cifras-origen">
                {{ extinta.documentos }} documentos · siguen abiertos: no se dan de baja ni se
                provisionan
              </p>
            </div>
            <div class="dos-cifras-celda">
              <p class="dos-cifras-rotulo">Pendiente total de la cartera</p>
              <p class="dos-cifras-valor">{{ formatEuros(calidad.importeTotal) }} €</p>
              <p class="dos-cifras-origen">{{ calidad.documentos }} documentos abiertos</p>
            </div>
          </div>
          <p class="dos-cifras-explicacion" v-if="extinta">{{ extinta.texto }}</p>
          <!-- Nombrarlos solo cuando hay alguno: la línea de arriba ya dice que no hay ninguno, y
               una tabla con la cabecera sola debajo de un «0 documentos» no añade nada. -->
          <div class="tabla-scroll" v-if="extinta && extinta.listado.length">
            <table class="tabla">
              <thead>
                <tr>
                  <th scope="col">Cliente</th>
                  <th scope="col">Documento</th>
                  <th scope="col" class="num">Nº</th>
                  <th scope="col">Vencimiento</th>
                  <th scope="col" class="num">Saldo pendiente</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="doc in extinta.listado" :key="'ex-' + doc.id"
                  :class="{'fila-abono': doc.tipo === 'ABONO'}">
                  <td>{{ texto(doc.cliente) }}</td>
                  <td>
                    {{ texto(doc.codigo) }}
                    <span class="etiqueta-abono" v-if="doc.tipo === 'ABONO'">abono</span>
                  </td>
                  <td class="num">{{ numero(doc.numero) }}</td>
                  <td>{{ formatFecha(doc.fechaVencimiento) }}</td>
                  <td class="num" :class="{'celda-falta': doc.saldoPendiente === null || doc.saldoPendiente === undefined}">
                    {{ formatEurosConUnidad(doc.saldoPendiente) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Los dos mismos pies que la alerta de al lado, y por el mismo motivo: las dos salen
               del mismo cálculo de la API, con el mismo tope de la lista y el mismo hueco posible
               de «Saldo Vencido». Sin ellos, esta cifra callaría lo que la de arriba dice —un
               importe que no cuenta todos los documentos, y una lista recortada en silencio—, y es
               justo lo que D-COB-8 no admite. -->
          <p class="nota-dato nota-dato-pie" v-if="extinta && extinta.documentosSinSaldo">
            {{ extinta.documentosSinSaldo }} de estos documentos han llegado sin «Saldo Vencido»,
            así que cuentan como documento pero no aportan euros al importe de arriba.
          </p>
          <p class="nota-dato nota-dato-pie" v-if="extinta && extinta.documentosOmitidos">
            Se nombran los {{ extinta.listado.length }} de mayor saldo;
            {{ extinta.documentosOmitidos }} no caben aquí. Las cifras de arriba cuentan todos.
            <button type="button" class="boton-gestiones"
              @click="abrirDocumentosDeLaCalidad(extinta)">
              Ver la lista completa
            </button>
          </p>

          <!-- b) D-COB-8 · LO QUE FALTA POR CUMPLIMENTAR. Una fila por campo, con los documentos
               afectados, el porcentaje sobre el total de abiertos y el acceso a la lista. -->
          <p class="bloque-subtitulo bloque-subtitulo-separado">Lo que falta por cumplimentar</p>
          <div class="tabla-scroll">
            <table class="tabla">
              <thead>
                <tr>
                  <th scope="col">Campo</th>
                  <th scope="col" class="num">Documentos</th>
                  <th scope="col" class="num">% de la cartera</th>
                  <th scope="col">Dónde está el dato</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="hueco in calidad.huecos" :key="hueco.campo"
                  :class="{'fila-sin-dato': !hueco.documentos}">
                  <th scope="row">
                    {{ hueco.etiqueta }}
                    <!-- Criterio 4 · la fila que está vacía por un motivo que no es un descuido de
                         quien graba lo dice AQUÍ, junto a su cifra, y no en un aviso al principio
                         de la pantalla: quien lee el 96 % tiene que encontrar el porqué al lado. -->
                    <span class="celda-nota celda-envuelve" v-if="hueco.motivo">
                      {{ hueco.motivo }}
                    </span>
                  </th>
                  <td class="num">{{ hueco.documentos }}</td>
                  <!-- Una cartera vacía no tiene un 0 % de huecos: no tiene porcentaje. -->
                  <td class="num" :class="{'celda-falta': hueco.porcentaje === null || hueco.porcentaje === undefined}">
                    {{ formatPorcentaje(hueco.porcentaje) }}
                  </td>
                  <td class="celda-envuelve">{{ texto(hueco.dondeEsta) }}</td>
                  <td>
                    <!-- Sin documentos no se dibuja el botón: una lista de cero documentos no es
                         una pantalla a la que llegar, y un botón que no hace nada al pulsarlo es
                         peor que no tener botón. Se dice en su sitio, no se queda en blanco. -->
                    <button type="button" class="boton-gestiones" v-if="hueco.documentos"
                      @click="abrirDocumentosDeLaCalidad(hueco)">
                      Ver la lista
                    </button>
                    <span class="celda-falta" v-else>nada que listar</span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <!-- El denominador lo manda el servidor y la pantalla NO lo cuenta: es la cifra con
                     la que se cotejan los porcentajes contra el listado del día, igual que el pie
                     de la escalera y el de las personas. -->
                <tr>
                  <th scope="row">Total de documentos abiertos</th>
                  <td class="num">{{ calidad.documentos }}</td>
                  <td class="num"></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p class="nota-dato nota-dato-pie">{{ calidad.notaDenominador }}</p>

          <!-- D-COB-12 · CUÁLES SON las gestiones que no se han podido atribuir. El aviso de
               cabecera de esta pantalla dice cuántas hay; aquí están.

               Esta comprobación también sale a cero y lo dice, por lo mismo que la de «Cobrado
               total» (dirección, 20/10/2026): era la otra del bloque que desaparecía al no
               encontrar nada. Lo que no se dibuja a cero es la tabla, igual que allí.

               El aviso de CABECERA de la pantalla no cambia y sigue saliendo solo cuando hay
               alguna: ese es una alerta sobre una cifra que se queda corta —el nº de gestiones de
               la tabla de la cartera—, no el parte de una comprobación.

               Qué se enseña aquí y qué no lo decide el servidor, y es una decisión tomada: de un
               vínculo ambiguo —uno que nombra un documento que se ve y cuyas otras señas casan
               solo con documentos ajenos— se publican las cuatro columnas tal cual, aceptando que
               el nº y el tipo podrían ser de otro (dirección, opción A del 19/10/2026). Lo que
               acota el riesgo es que de un documento ajeno NO llega nada leído de él: ni cliente,
               ni importe, ni fechas. Así que esta tabla no añade columnas de la factura señalada:
               hacerlo cambiaría ese límite sin que nadie lo decida. El razonamiento está en
               `FactCobroCarteraDatoSucio.sePuedeInformar`.

               La columna «Vínculo» no es una de esas: dice qué vínculo de su propia tarea es la
               fila, no se lee de ningún documento y no dice nada de ninguno. -->
          <template v-if="calidad.notaGestionesSinAtribuir">
            <p class="bloque-subtitulo bloque-subtitulo-separado">
              Gestiones de cobro sin atribuir
              <!-- Las DOS cifras de la frase son del servidor y miden el mismo conjunto: contando
                   las filas que han llegado, con el tope recortando salía «53 gestiones en 50
                   vínculos», que además es imposible —cada gestión trae al menos un vínculo—. El
                   pie de abajo dice cuántos de esos vínculos no caben en la tabla. -->
              <span class="bloque-nota" v-if="haySinAtribuir">
                · {{ calidad.gestionesSinAtribuirDistintas }} gestiones
                en {{ vinculosSinAtribuir }} vínculos
              </span>
              <span class="bloque-nota" v-else>
                · ninguna ({{ vinculosSinAtribuir }} vínculos)
              </span>
            </p>
            <div class="nota-dato" :class="{'nota-dato-alerta': haySinAtribuir}">
              <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"
                v-if="haySinAtribuir"></f7-icon>
              <f7-icon f7="checkmark_circle" class="nota-dato-icono" v-else></f7-icon>
              <span>{{ calidad.notaGestionesSinAtribuir }}</span>
            </div>
            <div class="tabla-scroll" v-if="haySinAtribuir">
              <table class="tabla">
                <thead>
                  <tr>
                    <th scope="col">Tarea</th>
                    <!-- Qué vínculo de esa tarea es. La misma T0045 sale en varias filas en cuanto
                         trae varios vínculos ambiguos, y puede salir dos veces con las MISMAS
                         señas: la migración convertía la lista de números sin deduplicarla, así que
                         una tarea puede llevar el mismo número dos veces. Sin esta columna esas dos
                         filas se leen como un fallo de la pantalla en vez de como el dato sucio que
                         son, que es lo que D-COB-8 no quiere. -->
                    <th scope="col" class="num">Vínculo</th>
                    <th scope="col">Código que traía</th>
                    <th scope="col" class="num">Nº</th>
                    <th scope="col">Tipo</th>
                    <th scope="col">Por qué no se atribuye</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(gestion, indice) in gestionesSinAtribuir" :key="'g-' + indice">
                    <td>{{ texto(gestion.idGestion) }}</td>
                    <td class="num">{{ numero(gestion.vinculo) }}</td>
                    <td :class="{'celda-falta': !gestion.codigo}">{{ texto(gestion.codigo) }}</td>
                    <td class="num">{{ numero(gestion.numero) }}</td>
                    <td :class="{'celda-falta': !gestion.tipo}">{{ texto(gestion.tipo) }}</td>
                    <td class="celda-envuelve">{{ texto(gestion.porQue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- El mismo botón que las dos alertas de arriba, y por el mismo motivo: este pie dice
                 que faltan vínculos y sin él los que el tope recorta no se podrían ver en ningún
                 sitio, que es justo lo contrario de lo que este bloque viene a hacer. Y solo cuando
                 el tope ha recortado algo: con la tabla entera arriba, abriría lo mismo otra vez. -->
            <p class="nota-dato nota-dato-pie" v-if="calidad.gestionesSinAtribuirOmitidas">
              Hay {{ calidad.gestionesSinAtribuirOmitidas }} vínculos más que no caben en esta
              tabla. El recuento de arriba cuenta todos.
              <button type="button" class="boton-gestiones" @click="abrirLosVinculosSinAtribuir()">
                Ver el listado completo
              </button>
            </p>
          </template>
        </bloque-plegable>
      </template>

      <!-- #202 · COMPORTAMIENTO POR FORMA DE PAGO · cómo paga de verdad cada condición de pago.
           De solo lectura: desde aquí no se cambia ningún plazo ni ninguna condición.

           Va donde la escalera y las personas, y por el mismo motivo: no lo recortan los filtros
           de abajo, así que su pendiente es el de la cartera que esa persona ve y se puede cotejar
           contra el listado de facturas pendientes del día sin acordarse de qué filtros había
           puestos.

           Es un componente con su propia lectura y su propio periodo —a diferencia de la escalera
           y las personas, que viajan dentro de la respuesta de la cartera— porque necesita las
           facturas del periodo de CUALQUIER estado, que es otro conjunto y otra consulta. Así
           mover su periodo no vuelve a pedir la cartera, ni al revés.

           EL CUADRO NO SE DESMONTA NUNCA, Y ESO ES LA REGLA, NO UN DETALLE DE ESTA LÍNEA
           (revisión CODEX, rondas 1, 2 y 5: tres veces el mismo fondo). Lo que se repetía es que
           su vida colgaba del estado de la cartera, y cada vez que la cartera cambiaba de estado
           el cuadro perdía lo suyo:

             · ronda 1 · dentro del `v-if` de arriba, cada filtro ponía `cargando` a true, lo
               destruía, y al volver su `mounted` pedía otra vez `/comportamiento-pago` con el
               periodo por omisión: se llevaba por delante el periodo elegido;
             · ronda 2 · al quedarse montado, el botón de recargar de la cabecera ya no lo tocaba
               —su `mounted` no vuelve a correr—, así que su propio fallo se quedaba puesto;
             · ronda 5 · con `v-if="dto"`, una recarga de la cartera que FALLA pone `dto` a null
               y volvía a destruirlo, con el agravante de que el `ref` desaparecía y el botón de
               la cabecera tampoco podía reintentarlo.

           Así que aquí solo se ESCONDE, nunca se desmonta: su estado —el periodo elegido, sus
           cifras y su propio aviso— es suyo y no lo tira ningún estado de la cartera. Se esconde
           mientras la pantalla no esté enseñando cifras, porque entonces lo que se ve es el
           preloader o el aviso de la cartera y un cuadro suelto debajo contradiría al aviso.
           Montado y escondido no pide nada: solo pide en su `mounted` y cuando se le dice. -->
      <!--
        #749 · El triángulo de este bloque lo pinta el componente, pero el estado lo guarda ESTA
        pantalla y se le pasa: todos los bloques del Centro de Cobros se recuerdan en la misma
        entrada del navegador, y con dos dueños escribiendo en ella el último en guardar borraría
        los bloques del otro.
      -->
      <cuadro-comportamiento-pago ref="cuadroComportamiento" v-show="dto && !cargando"
        :abierto="plegado.comportamiento" @alternar="alternarBloque('comportamiento')" />

      <!-- #199 · LA GESTIÓN · facturas sin ninguna gestión, silencios y actividad por gestor y
           semana, sacados de las tareas de gestión de cobro. Con su propia lectura, y montado y
           escondido por lo mismo que el cuadro de comportamiento de arriba: su estado —las
           semanas elegidas, el N que se está escribiendo— no lo tira ningún estado de la cartera.

           #830 · Con su triángulo, y con el estado aquí por lo mismo que el cuadro de arriba: todos
           los bloques de la pantalla se recuerdan en la misma entrada del navegador. -->
      <cuadro-gestion-cobro ref="cuadroGestion" v-show="dto && !cargando"
        :abierto="plegado.gestion" @alternar="alternarBloque('gestion')" />

      <!-- #749 · el mismo cambio que arriba, y el que de verdad se notaba: los filtros viven dentro
           de este `template`, así que la recarga que apagaba la condición se llevaba también el
           panel con el que se acababa de filtrar. -->
      <template v-if="dto">

        <!-- FILTROS -->
        <!-- #749 · el bloque ya se plegaba, pero con un galón arriba/abajo y sin recordar nada.
             Ahora lleva el mismo triángulo que los demás bloques —abajo abierto, a la derecha
             cerrado— y su cabecera cerrada sigue diciendo qué filtros hay puestos, que es la regla
             de dirección de que un filtro activo siempre se ve aunque su bloque esté cerrado. -->
        <bloque-plegable titulo="Filtros" :abierto="plegado.filtros" :resumen="resumenFiltros"
          @alternar="alternarBloque('filtros')">
          <div class="filtros-rejilla">
              <label class="filtro">
                <span class="filtro-rotulo">Año de emisión</span>
                <select v-model="filtro.anioEmision" @change="cambiarAnioEmision">
                  <option :value="null">Todos</option>
                  <option v-for="anio in dto.aniosDisponibles" :key="anio" :value="anio">{{ anio }}</option>
                </select>
              </label>
              <label class="filtro">
                <span class="filtro-rotulo">Mes de emisión</span>
                <select v-model="filtro.mesEmision" @change="aplicarFiltros" :disabled="!filtro.anioEmision">
                  <option :value="null">Todos</option>
                  <option v-for="(nombre, indice) in nombresMes" :key="nombre" :value="indice + 1">{{ nombre }}</option>
                </select>
                <!-- Deshabilitado sin año porque la API no filtra por un mes suelto: «marzo de
                     todos los ejercicios» no es ninguno de los dos recortes de la pantalla. -->
                <span class="filtro-ayuda" v-if="!filtro.anioEmision">Elige antes un año</span>
              </label>
              <!-- LOS TRES DE LISTA LARGA · se buscan escribiendo, que es la regla permanente que
                   fijó dirección el 18/10/2026 (CLAUDE.md §9): «todo filtro o selector de personas
                   —o de cualquier lista larga— se puede buscar escribiendo. Por nombre, apellidos y
                   código». El de clientes trae un desplegable de cientos de entradas.

                   El buscador NO filtra la cartera: solo acorta la lista del desplegable, así que
                   escribir en él no pide nada al servidor. Y el rótulo va en un `label` con `for`
                   en vez de envolver los dos controles: un `label` con dos controles dentro rotula
                   solo el primero, y el desplegable —que es el filtro— se quedaría sin nombre. -->
              <div class="filtro">
                <label class="filtro-rotulo" for="cobros-empleado-ventas">Empleado de ventas</label>
                <input type="search" class="filtro-busca" v-model="busca.empleadoVentas"
                  aria-label="Buscar empleado de ventas por nombre o código"
                  placeholder="Buscar por nombre o código" />
                <select id="cobros-empleado-ventas" v-model="filtro.empleadoVentas" @change="aplicarFiltros">
                  <option :value="null">Todos</option>
                  <option v-for="opcion in opcionesVentasBuscadas" :key="opcion.clave" :value="opcion.clave">
                    {{ opcion.etiqueta }} ({{ opcion.documentos }})
                  </option>
                </select>
                <span class="filtro-ayuda" v-if="sinCoincidencias('empleadoVentas', opcionesVentasBuscadas)">
                  Ningún empleado de ventas coincide con «{{ busca.empleadoVentas }}»
                </span>
              </div>
              <div class="filtro">
                <label class="filtro-rotulo" for="cobros-gestor-cobro">Gestor de cobro</label>
                <input type="search" class="filtro-busca" v-model="busca.gestorCobro"
                  aria-label="Buscar gestor de cobro por nombre o código"
                  placeholder="Buscar por nombre o código" />
                <select id="cobros-gestor-cobro" v-model="filtro.gestorCobro" @change="aplicarFiltros">
                  <option :value="null">Todos</option>
                  <option v-for="opcion in opcionesGestorBuscadas" :key="opcion.clave" :value="opcion.clave">
                    {{ opcion.etiqueta }} ({{ opcion.documentos }})
                  </option>
                </select>
                <span class="filtro-ayuda" v-if="!(dto.opcionesGestorCobro || []).length">
                  Todavía no hay ninguna factura con gestor asignado
                </span>
                <span class="filtro-ayuda" v-else-if="sinCoincidencias('gestorCobro', opcionesGestorBuscadas)">
                  Ningún gestor de cobro coincide con «{{ busca.gestorCobro }}»
                </span>
              </div>
              <div class="filtro">
                <label class="filtro-rotulo" for="cobros-cliente">Cliente</label>
                <input type="search" class="filtro-busca" v-model="busca.cliente"
                  aria-label="Buscar cliente por nombre o código"
                  placeholder="Buscar por nombre o código" />
                <select id="cobros-cliente" v-model="filtro.cliente" @change="aplicarFiltros">
                  <option :value="null">Todos</option>
                  <option v-for="opcion in opcionesClienteBuscadas" :key="opcion.clave" :value="opcion.clave">
                    {{ opcion.etiqueta }} ({{ opcion.documentos }})
                  </option>
                </select>
                <span class="filtro-ayuda" v-if="sinCoincidencias('cliente', opcionesClienteBuscadas)">
                  Ningún cliente coincide con «{{ busca.cliente }}»
                </span>
              </div>
              <label class="filtro">
                <span class="filtro-rotulo">Estado de gestión</span>
                <select v-model="filtro.estadoGestion" @change="aplicarFiltros">
                  <option :value="null">Todos</option>
                  <option v-for="opcion in dto.opcionesEstadoGestion" :key="opcion.clave" :value="opcion.clave">
                    {{ opcion.etiqueta }} ({{ opcion.documentos }})
                  </option>
                </select>
              </label>
              <label class="filtro">
                <span class="filtro-rotulo">Forma de pago</span>
                <select v-model="filtro.formaPago" @change="aplicarFiltros">
                  <option :value="null">Todas</option>
                  <option v-for="opcion in dto.opcionesFormaPago" :key="opcion.clave" :value="opcion.clave">
                    {{ opcion.etiqueta }} ({{ opcion.documentos }})
                  </option>
                </select>
              </label>
              <label class="filtro">
                <span class="filtro-rotulo">Tramo de antigüedad</span>
                <select v-model="filtro.tramo" @change="aplicarFiltros">
                  <option :value="null">Todos</option>
                  <option v-for="opcion in dto.opcionesTramo" :key="opcion.clave" :value="opcion.clave">
                    {{ opcion.etiqueta }} ({{ opcion.documentos }})
                  </option>
                </select>
              </label>
            </div>
            <div class="conmutadores">
              <label class="conmutador-check">
                <input type="checkbox" v-model="filtro.soloVencido" @change="aplicarFiltros" />
                <span>Solo vencido</span>
              </label>
              <!-- CRITERIO 7 · con el conmutador puesto, se elige y se VE con qué criterio filtra. -->
              <div class="criterio-vencido" v-if="filtro.soloVencido">
                <button type="button" class="criterio-btn" :class="{'criterio-btn-activo': filtro.vencidoIncluyeAbonos}"
                  :aria-pressed="filtro.vencidoIncluyeAbonos ? 'true' : 'false'"
                  @click="cambiarCriterioVencido(true)">Con abonos</button>
                <button type="button" class="criterio-btn" :class="{'criterio-btn-activo': !filtro.vencidoIncluyeAbonos}"
                  :aria-pressed="!filtro.vencidoIncluyeAbonos ? 'true' : 'false'"
                  @click="cambiarCriterioVencido(false)">Solo facturas</button>
              </div>
              <label class="conmutador-check">
                <input type="checkbox" v-model="filtro.sinGestorAsignado" @change="aplicarFiltros" />
                <span>Sin gestor asignado</span>
              </label>
              <button type="button" class="boton-limpiar" @click="limpiarFiltros">Quitar filtros</button>
            </div>
            <p class="criterio-texto">{{ dto.criterioVencido }}</p>
        </bloque-plegable>

        <!-- GRÁFICO DE ANTIGÜEDAD · barras horizontales partidas en dos colores, escala común a
             todas las filas y el nº de documentos a la derecha.

             Es además el PRIMER NIVEL de la navegación por antigüedad: con el recorrido por
             antigüedad y sin nada elegido todavía, pinchar una barra baja a sus empleados de
             ventas. Así no hay dos listas de tramos con los mismos números. -->
        <bloque-plegable ref="bloqueAntiguedad" titulo="Antigüedad de la cartera"
          :nota="tramoPinchable ? '· pincha un tramo para bajar' : ''"
          :abierto="plegado.antiguedad" :resumen="resumenAntiguedad" :refrescando="!!refrescando"
          @alternar="alternarBloque('antiguedad')">
          <div class="grafico-tramos">
            <div v-for="tramo in dto.tramos" :key="tramo.tramo" class="tramo-fila"
              :class="{'tramo-fila-pinchable': tramoSePuedePinchar(tramo)}"
              @click="pincharTramo(tramo)">
              <span class="tramo-etiqueta">{{ tramo.etiqueta }}</span>
              <span class="tramo-barra">
                <span class="tramo-parte tramo-parte-plazo" :style="{ width: ancho(tramo.importeEnPlazo) }"></span>
                <span class="tramo-parte tramo-parte-vencido" :style="{ width: ancho(tramo.importeVencido) }"></span>
                <span class="tramo-parte tramo-parte-sinvto" :style="{ width: ancho(tramo.importeSinVencimiento) }"></span>
              </span>
              <span class="tramo-importe">{{ formatEuros(tramo.importeTotal) }} €</span>
              <span class="tramo-docs">{{ tramo.documentos }}</span>
            </div>
          </div>
          <div class="leyenda">
            <span class="leyenda-item"><i class="leyenda-color leyenda-plazo"></i>En plazo</span>
            <span class="leyenda-item"><i class="leyenda-color leyenda-vencido"></i>Vencido</span>
            <span class="leyenda-item" v-if="haySinVencimiento">
              <i class="leyenda-color leyenda-sinvto"></i>Sin fecha de vencimiento
            </span>
          </div>
        </bloque-plegable>

        <!-- NAVEGACIÓN POR NIVELES · sin salir de la pantalla -->
        <bloque-plegable titulo="Recorrido por la cartera" :abierto="plegado.navegacion"
          :resumen="resumenNavegacion" :refrescando="!!refrescando"
          @alternar="alternarBloque('navegacion')">
          <div class="recorridos">
            <button type="button" class="recorrido-btn" :class="{'recorrido-btn-activo': recorrido === 'ANTIGUEDAD'}"
              :aria-pressed="recorrido === 'ANTIGUEDAD' ? 'true' : 'false'"
              @click="cambiarRecorrido('ANTIGUEDAD')">Por antigüedad</button>
            <button type="button" class="recorrido-btn" :class="{'recorrido-btn-activo': recorrido === 'GESTOR'}"
              :aria-pressed="recorrido === 'GESTOR' ? 'true' : 'false'"
              @click="cambiarRecorrido('GESTOR')">Por gestor de cobro</button>
          </div>
          <div class="migas">
            <button type="button" class="miga" @click="volverA(0)">Todo</button>
            <template v-for="(paso, indice) in ruta">
              <span :key="'sep-' + indice" class="miga-sep">›</span>
              <button :key="'miga-' + indice" type="button" class="miga" @click="volverA(indice + 1)">
                {{ paso.etiqueta }}
              </button>
            </template>
          </div>

          <!-- El primer nivel del recorrido por antigüedad ES el gráfico de arriba, no una lista:
               aquí solo se dice dónde pinchar. Sin esto saldría el aviso de «no hay nada que
               enseñar» debajo de un gráfico con cifras, que es contradecir lo que se está viendo.
               Las tres ramas van con su propia condición y no en cadena: una cadena de
               `v-else-if` con estos comentarios en medio depende de cómo se compilen. -->
          <!-- #830 · en tamaño de texto normal y no en la letra pequeña de `bloque-vacio`: con esa
               letra, el bloque se leía vacío y JJCS lo tomó por un fallo de desplazamiento («cuando
               está por antigüedad no se puede bajar nada»). Es la regla del 05/10 (CLAUDE.md §9):
               lo que hay que hacer para seguir se dice aquí dentro, junto a lo que parece parado. -->
          <div class="aviso-primer-nivel" v-if="nivelEsElGrafico && !graficoSinDocumentos">
            <p class="aviso-primer-nivel-texto">
              Elige un tramo del gráfico «Antigüedad de la cartera» para ver sus empleados de
              ventas.
            </p>
            <!-- #749 · y si ese gráfico está plegado, la frase no puede quedarse señalando a un
                 bloque cerrado: dos partes de la misma pantalla diciendo lo contrario la una de la
                 otra es justo lo que dirección prohibió. #830 · y no basta con decir que está
                 cerrado: se abre desde aquí mismo. -->
            <p class="aviso-primer-nivel-texto" v-if="!plegado.antiguedad">
              Ese gráfico está cerrado ahora.
              <button type="button" class="boton-abrir-grafico" @click="abrirGraficoAntiguedad">
                Abrir el gráfico
              </button>
            </p>
          </div>
          <!-- Sin ningún documento en el gráfico no hay tramo que elegir, y mandar pinchar en él
               sería mandar a la persona a un sitio del que no sale nada. -->
          <div class="aviso-primer-nivel" v-if="nivelEsElGrafico && graficoSinDocumentos">
            <p class="aviso-primer-nivel-texto">
              El gráfico «Antigüedad de la cartera» no tiene ningún documento, así que no hay ningún
              tramo que elegir.
            </p>
          </div>

          <!-- Un nivel intermedio: la lista de grupos del nivel al que toca bajar. -->
          <template v-if="!enLaTabla && !nivelEsElGrafico">
            <p class="bloque-titulo">{{ tituloNivel }}</p>
            <p class="bloque-vacio" v-if="!listaNivel.length">
              No hay nada que enseñar en este nivel con los filtros puestos.
            </p>
            <div class="grupos">
              <div v-for="grupo in listaNivel" :key="grupo.clave === null ? 'sin-valor' : grupo.clave"
                class="grupo-fila" @click="bajarA(dimensionActual, grupo.clave, grupo.etiqueta)">
                <span class="grupo-etiqueta">{{ grupo.etiqueta }}</span>
                <span class="grupo-importe">{{ formatEuros(grupo.importeTotal) }} €</span>
                <span class="grupo-detalle">
                  {{ grupo.documentos }} docs ·
                  vencido {{ formatEuros(grupo.importeVencido) }} € ({{ grupo.documentosVencido }}) ·
                  con gestor {{ grupo.documentosConGestor }} ·
                  gestionadas {{ grupo.documentosGestionados }}
                </span>
                <f7-icon f7="chevron_right" class="grupo-chevron"></f7-icon>
              </div>
            </div>
          </template>

          <!-- El último nivel: la tabla de facturas. #749 · con su propio triángulo, que es uno de
               los grupos que dirección pidió por su nombre. Dentro del bloque del recorrido y sin
               caja: es el último nivel de esta navegación, no un bloque aparte. -->
          <template v-if="enLaTabla">
            <bloque-plegable titulo="Facturas" :abierto="plegado.facturas"
              :resumen="resumenFacturas" :refrescando="!!refrescando" :caja="false" sub
              @alternar="alternarBloque('facturas')">
            <p class="bloque-vacio" v-if="!filas.length">
              No hay facturas con los filtros puestos.
            </p>
            <p class="bloque-nota" v-if="dto.filasOmitidas">
              Se enseñan las {{ filas.length }} de mayor saldo pendiente;
              {{ dto.filasOmitidas }} no caben en la tabla. Las cifras de arriba cuentan todas.
            </p>
            <div class="tabla-scroll" v-if="filas.length">
              <table class="tabla">
                <thead>
                  <tr>
                    <th scope="col">Cliente</th>
                    <th scope="col">Factura</th>
                    <th scope="col">Emisión</th>
                    <th scope="col" class="num">Días</th>
                    <th scope="col">Vencimiento</th>
                    <th scope="col" class="num">Retraso</th>
                    <th scope="col" class="num">Saldo pendiente</th>
                    <th scope="col">Forma de pago</th>
                    <th scope="col">Gestor (PGC)</th>
                    <!-- #189 · el contador por factura de los cambios de gestor, que es el detalle
                         de la columna «3 o más cambios» de la tabla por gestor de arriba. -->
                    <th scope="col" class="num">Cambios de gestor</th>
                    <th scope="col" class="num">Gestiones</th>
                    <th scope="col" class="num">Últ. gestión</th>
                    <th scope="col">Estado de gestión</th>
                    <th scope="col">F. probable</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="fila in filas" :key="fila.id" :class="{'fila-abono': fila.tipo === 'ABONO'}">
                    <td>{{ texto(fila.cliente) }}</td>
                    <td>
                      {{ texto(fila.codigo) }}
                      <span class="etiqueta-abono" v-if="fila.tipo === 'ABONO'">abono</span>
                    </td>
                    <td>{{ formatFecha(fila.fechaEmision) }}</td>
                    <td class="num">{{ numero(fila.diasAntiguedad) }}</td>
                    <td>{{ formatFecha(fila.fechaVencimiento) }}</td>
                    <td class="num" :class="{'num-alerta': fila.diasRetraso}">{{ numero(fila.diasRetraso) }}</td>
                    <td class="num">{{ formatEuros(fila.saldoPendiente) }}</td>
                    <td>{{ texto(fila.formaPago) }}</td>
                    <td :class="{'celda-falta': !fila.gestorCobro}">{{ texto(fila.gestorCobro) }}</td>
                    <td class="num" :class="{'num-alerta': seHaPasadoDeMano(fila)}">
                      {{ fila.cambiosDeGestor }}
                      <span class="marca-edad" v-if="seHaPasadoDeMano(fila)">se pasa de mano</span>
                    </td>
                    <td class="num">{{ fila.gestiones }}</td>
                    <td class="num">{{ numero(fila.diasUltimaGestion) }}</td>
                    <td>
                      {{ texto(fila.estadoGestion) }}
                      <!-- D-COB-4 · lo que manda es la tarea posterior al vencimiento, no este
                           campo. Se enseña el campo tal cual Y la marca de si está gestionada de
                           verdad, porque los dos pueden decir cosas distintas. -->
                      <span class="marca-gestionada" v-if="fila.gestionada">gestionada</span>
                      <span class="marca-sin-gestionar" v-else>sin gestionar</span>
                    </td>
                    <td class="celda-falta">pendiente</td>
                    <td>
                      <button type="button" class="boton-gestiones" @click="abrirGestiones(fila)">
                        Gestiones
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="nota-dato nota-dato-pie">{{ dto.notaGestionada }}</p>
            <p class="nota-dato nota-dato-pie">{{ dto.notaFechaProbable }}</p>
            <p class="nota-dato nota-dato-pie">{{ dto.notaFormaPago }}</p>
            </bloque-plegable>
          </template>
        </bloque-plegable>
      </template>
    </f7-page-content>

    <!-- EL ÚLTIMO NIVEL · las tareas de gestión de cobro de una factura, dentro de la pantalla. -->
    <f7-popup class="popup-gestiones" v-if="gestiones.abierto" :opened="true" swipe-to-close
      @popup:closed="gestiones.abierto = false">
      <f7-page>
        <f7-navbar :title="gestiones.titulo">
          <f7-nav-right><f7-link popup-close>Cerrar</f7-link></f7-nav-right>
        </f7-navbar>
        <f7-block v-if="gestiones.cargando" class="text-align-center"><f7-preloader></f7-preloader></f7-block>
        <f7-block v-if="gestiones.error" class="aviso-error">{{ gestiones.error }}</f7-block>
        <f7-block v-if="!gestiones.cargando && !gestiones.error && !gestiones.lista.length">
          Esta factura no tiene ninguna tarea de gestión de cobro.
        </f7-block>
        <f7-block v-if="gestiones.lista.length">
          <div v-for="gestion in gestiones.lista" :key="gestion.id" class="gestion-fila">
            <p class="gestion-cabecera">
              <strong>{{ formatFecha(gestion.fecha) }}</strong>
              <span class="gestion-estado">{{ texto(gestion.estado) }}</span>
              <span class="gestion-cuenta" v-if="gestion.cuentaParaGestionada === true">
                posterior al vencimiento
              </span>
              <span class="gestion-nocuenta" v-else-if="gestion.cuentaParaGestionada === false">
                anterior al vencimiento: no cuenta como gestionada
              </span>
              <span class="gestion-nocuenta" v-else>
                la factura no tiene fecha de vencimiento: no se puede decidir
              </span>
            </p>
            <p class="gestion-detalle">
              {{ texto(gestion.codigo) }} · {{ texto(gestion.descripcion) }}
              <span v-if="gestion.empleado">· {{ gestion.empleado }}</span>
            </p>
            <p class="gestion-detalle" v-if="gestion.comentarios">{{ gestion.comentarios }}</p>
            <p class="gestion-origen">Fecha tomada de: {{ gestion.origenFecha }}</p>
          </div>
        </f7-block>
      </f7-page>
    </f7-popup>

    <!-- #201 · LA LISTA DE UNA FILA DE LA CALIDAD DEL DATO, dentro de la pantalla.
         Enseña cuántos documentos trae: es el número que se compara con el de la fila para
         comprobar el criterio 3, y por eso se escribe aquí y no se da por supuesto. -->
    <f7-popup class="popup-gestiones" v-if="documentos.abierto" :opened="true" swipe-to-close
      @popup:closed="documentos.abierto = false">
      <f7-page>
        <f7-navbar :title="documentos.titulo">
          <f7-nav-right><f7-link popup-close>Cerrar</f7-link></f7-nav-right>
        </f7-navbar>
        <f7-block v-if="documentos.cargando" class="text-align-center"><f7-preloader></f7-preloader></f7-block>
        <f7-block v-if="documentos.error" class="aviso-error">{{ documentos.error }}</f7-block>
        <!-- Este popup sirve a las SIETE filas del bloque, también a las dos de incoherencia, así
             que no puede decir «sin cumplimentar»: la lista vacía de «Cobrado total y sigue
             abierta» no significa eso. Y el servidor vuelve a leer la cartera al pedirla, así que
             vacía con la fila diciendo 7 es que la cartera ha cambiado, no que la fila mienta. -->
        <f7-block v-if="!documentos.cargando && !documentos.error && !documentos.lista.length">
          Ahora mismo no hay ningún documento en esta fila. Si la cifra de la pantalla decía otra
          cosa, la cartera ha cambiado desde que se cargó: vuelve a cargarla para verla al día.
        </f7-block>
        <f7-block v-if="documentos.lista.length">
          <p class="bloque-nota">{{ documentos.lista.length }} documentos</p>
          <div class="tabla-scroll">
            <table class="tabla">
              <thead>
                <tr>
                  <th scope="col">Cliente</th>
                  <th scope="col">Documento</th>
                  <th scope="col" class="num">Nº</th>
                  <th scope="col">Vencimiento</th>
                  <th scope="col" class="num">Saldo pendiente</th>
                  <th scope="col">Estado de gestión</th>
                </tr>
              </thead>
              <tbody>
                <!-- Sin el botón de «Gestiones» que sí tienen las tablas de la pantalla, y es a
                     propósito: abrirlo desde aquí sería un segundo popup encima de este, y el
                     apilado de los modales de Framework7 no se puede comprobar en este repo. Lo que
                     esta lista tiene que dar es con qué encontrar el documento para ir a
                     rellenarlo, y eso son el cliente y el código. -->
                <tr v-for="doc in documentos.lista" :key="doc.id"
                  :class="{'fila-abono': doc.tipo === 'ABONO'}">
                  <td>{{ texto(doc.cliente) }}</td>
                  <td>
                    {{ texto(doc.codigo) }}
                    <span class="etiqueta-abono" v-if="doc.tipo === 'ABONO'">abono</span>
                  </td>
                  <td class="num">{{ numero(doc.numero) }}</td>
                  <td>{{ formatFecha(doc.fechaVencimiento) }}</td>
                  <td class="num" :class="{'celda-falta': doc.saldoPendiente === null || doc.saldoPendiente === undefined}">
                    {{ formatEurosConUnidad(doc.saldoPendiente) }}
                  </td>
                  <td :class="{'celda-falta': !doc.estadoGestion}">{{ texto(doc.estadoGestion) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </f7-block>
      </f7-page>
    </f7-popup>

    <!-- #201 · EL LISTADO ENTERO DE LOS VÍNCULOS SIN ATRIBUIR, dentro de la pantalla.
         Las columnas son las de la tabla del bloque y no las de un documento: lo que estos vínculos
         traen son las señas con las que encontrar la tarea y el documento al que creían apuntar. -->
    <f7-popup class="popup-gestiones" v-if="vinculos.abierto" :opened="true" swipe-to-close
      @popup:closed="vinculos.abierto = false">
      <f7-page>
        <f7-navbar title="Gestiones de cobro sin atribuir">
          <f7-nav-right><f7-link popup-close>Cerrar</f7-link></f7-nav-right>
        </f7-navbar>
        <f7-block v-if="vinculos.cargando" class="text-align-center"><f7-preloader></f7-preloader></f7-block>
        <f7-block v-if="vinculos.error" class="aviso-error">{{ vinculos.error }}</f7-block>
        <!-- Vacío con el pie diciendo que faltaban vínculos es que la cartera ha cambiado, no que el
             pie mintiera: el servidor vuelve a leerla y a cruzar las gestiones al pedir esto. -->
        <f7-block v-if="!vinculos.cargando && !vinculos.error && !vinculos.lista.length">
          Ahora mismo no hay ningún vínculo sin atribuir. Si la pantalla decía otra cosa, la cartera
          ha cambiado desde que se cargó: vuelve a cargarla para verla al día.
        </f7-block>
        <f7-block v-if="vinculos.lista.length">
          <p class="bloque-nota">{{ vinculos.lista.length }} vínculos</p>
          <div class="tabla-scroll">
            <table class="tabla">
              <thead>
                <tr>
                  <th scope="col">Tarea</th>
                  <!-- Las mismas columnas que la tabla del bloque, y por eso también esta: es el
                       mismo listado sin el tope. -->
                  <th scope="col" class="num">Vínculo</th>
                  <th scope="col">Código que traía</th>
                  <th scope="col" class="num">Nº</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Por qué no se atribuye</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(gestion, indice) in vinculos.lista" :key="'gv-' + indice">
                  <td>{{ texto(gestion.idGestion) }}</td>
                  <td class="num">{{ numero(gestion.vinculo) }}</td>
                  <td :class="{'celda-falta': !gestion.codigo}">{{ texto(gestion.codigo) }}</td>
                  <td class="num">{{ numero(gestion.numero) }}</td>
                  <td :class="{'celda-falta': !gestion.tipo}">{{ texto(gestion.tipo) }}</td>
                  <td class="celda-envuelve">{{ texto(gestion.porQue) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </f7-block>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>

<style scoped>
.sello-datos-bar { padding: 6px 16px 0; }

.linea-alcance, .nota-dato {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 16px 0;
  font-size: 12px;
  color: #4a5a78;
  line-height: 1.4;
}
.linea-alcance-icono, .nota-dato-icono { font-size: 16px; color: #185FA5; flex-shrink: 0; }
.nota-dato { background: #F2F7FD; border-radius: 8px; padding: 8px 10px; }
.nota-dato-pie { margin: 8px 0 0; }

/* #190 · la puerta a «Mi trabajo de hoy». Sin `gap`: el browserslist del repo no lo soporta. */
.bloque-trabajo-hoy { border-color: #534AB7; background: #FAFAFE; }
.trabajo-hoy-nota { font-size: 11px; color: #4a5a78; margin: 0; line-height: 1.45; }
.boton-trabajo-hoy { margin-top: 10px; }

/* D-COB-12 · en ámbar y no en rojo: no es un error de la pantalla, es dato que falta. Mismo tono
   que el aviso de la tarjeta de gestor, que es el otro «esto está incompleto». */
.nota-dato-alerta { background: #FDF4E3; color: #6B4A10; }
.nota-dato-icono-alerta { font-size: 16px; color: #9A6411; flex-shrink: 0; }

/* #193 · la entrada al informe de abonos abiertos. Mismo margen que las notas de arriba para que
   no reorganice nada de lo que ya había: es una línea más, no un bloque que mueva la pantalla. */
.entrada-informe { margin: 8px 16px 0; }
.entrada-informe-enlace {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: #F2F7FD;
  border: 1px solid #D6E4F5;
  border-radius: 8px;
  text-align: left;
  color: #1a2744;
}
.entrada-informe-icono { font-size: 20px; color: #185FA5; flex-shrink: 0; }
.entrada-informe-texto { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.entrada-informe-texto strong { font-size: 13px; color: #1a2744; }
.entrada-informe-texto small { font-size: 11px; color: #4a5a78; line-height: 1.4; }
.entrada-informe-flecha { font-size: 16px; color: #185FA5; flex-shrink: 0; }
/* Indicador de foco propio: sin esto, quien navega con el teclado no ve dónde está. */
.entrada-informe-enlace:focus-visible { outline: 2px solid #185FA5; outline-offset: 2px; }

.aviso-error {
  background: #FDECEA;
  border-radius: 8px;
  color: #8E2B20;
  font-size: 13px;
}

/* TARJETAS */
/* #749 · las tarjetas se plegan sin caja —ya tienen la suya y meterlas en otra las movería de
   sitio—, así que el rótulo con el triángulo necesita aquí el margen lateral que la caja le da al
   resto de los bloques. El hueco entre el rótulo y las tarjetas lo sigue poniendo el `padding` de
   la rejilla, que no se toca. */
.titulares-plegable { margin: 12px 16px 0; }
.titulares {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  padding: 12px 16px 0;
}
.titular {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 12px 14px;
}
.titular-aviso { border-color: #E0A33A; background: #FFFBF2; }
.titular-rotulo { font-size: 11px; color: #7c8db0; margin: 0; text-transform: uppercase; }
.titular-cifra { font-size: 22px; font-weight: 700; color: #1a2744; margin: 4px 0 0; }
.titular-cifra-alerta { color: #B23B2C; }
.titular-nota { font-size: 11px; color: #7c8db0; margin: 4px 0 0; line-height: 1.35; }
.titular-nota-aviso { color: #9A6411; }
/* El segundo dato de la tarjeta: la cuenta que no va en la cifra grande. Más marcado que una nota
   —es un número, no una explicación— y claramente por debajo del titular. */
.titular-nota-segundo { color: #4a5d86; font-weight: 600; }
.titular-nota-explica { font-size: 10px; color: #93a1bd; }
/* #188 · el enlace a la bandeja, dentro de la nota de aviso: hereda su tamaño y se subraya para que
   se lea como un enlace y no como parte de la frase. */
.enlace-bandeja { font-size: 11px; color: #185FA5; text-decoration: underline; display: inline; }

/* BLOQUES */
.bloque {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  margin: 12px 16px 0;
  padding: 12px 14px;
}
.bloque-titulo {
  font-size: 13px;
  font-weight: 600;
  color: #1a2744;
  margin: 0 0 8px;
}
.bloque-nota { font-size: 11px; color: #7c8db0; font-weight: 400; margin: 0 0 6px; }
.bloque-vacio { font-size: 12px; color: #7c8db0; margin: 4px 0; }

/* D-COB-11 */
.dos-cifras-fila {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}
.dos-cifras-rotulo { font-size: 11px; color: #7c8db0; margin: 0; }
.dos-cifras-valor { font-size: 18px; font-weight: 700; color: #1a2744; margin: 2px 0 0; }
.dos-cifras-valor-falta { color: #9aa7bd; }
.dos-cifras-origen { font-size: 11px; color: #7c8db0; margin: 2px 0 0; line-height: 1.35; }
.dos-cifras-explicacion {
  font-size: 12px;
  color: #4a5a78;
  margin: 10px 0 0;
  line-height: 1.45;
  border-top: 1px solid #eef1f6;
  padding-top: 8px;
}

/* FILTROS · la cabecera con el triángulo y el resumen de lo que hay puesto la pinta ahora
   `BloquePlegable`, que es el mismo control en todos los bloques de la pantalla (#749). */
.filtros-rejilla {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
}
.filtro { display: flex; flex-direction: column; gap: 3px; }
.filtro-rotulo { font-size: 11px; color: #7c8db0; }
.filtro select {
  font-size: 12px;
  padding: 5px 6px;
  border: 1px solid #d7dde8;
  border-radius: 6px;
  background: white;
  width: 100%;
}
.filtro-ayuda { font-size: 10px; color: #9aa7bd; }
/* #749 · el buscador de los tres desplegables de lista larga. Mismo alto y mismo borde que el
   desplegable al que acompaña: es parte del mismo control, no un campo más del formulario. */
.filtro-busca {
  font-size: 12px;
  padding: 5px 6px;
  border: 1px solid #d7dde8;
  border-radius: 6px;
  background: white;
  width: 100%;
  /* Un `input` dentro de Framework7 hereda tipografías de la lista: se declara la del filtro. */
  color: #1a2744;
}
.filtro-busca::placeholder { color: #9aa7bd; }
.conmutadores {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 10px;
}
.conmutador-check { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #1a2744; }
.criterio-vencido { display: inline-flex; border: 1px solid #d7dde8; border-radius: 6px; overflow: hidden; }
.criterio-btn {
  font-size: 11px;
  padding: 4px 10px;
  border: 0;
  background: white;
  color: #4a5a78;
}
.criterio-btn-activo { background: #185FA5; color: white; }
.boton-limpiar {
  font-size: 11px;
  padding: 4px 10px;
  border: 1px solid #d7dde8;
  border-radius: 6px;
  background: white;
  color: #4a5a78;
}
.criterio-texto { font-size: 11px; color: #4a5a78; margin: 8px 0 0; }

/* GRÁFICO */
.grafico-tramos { display: flex; flex-direction: column; gap: 6px; }
.tramo-fila { display: flex; align-items: center; gap: 8px; }
.tramo-fila-pinchable { cursor: pointer; }
.tramo-fila-pinchable:active { opacity: 0.6; }
.tramo-etiqueta { font-size: 11px; color: #4a5a78; width: 86px; flex-shrink: 0; }
.tramo-barra {
  flex: 1;
  display: flex;
  height: 16px;
  background: #f2f5fa;
  border-radius: 3px;
  overflow: hidden;
  min-width: 40px;
}
.tramo-parte { height: 100%; }
.tramo-parte-plazo { background: #0F6E56; }
.tramo-parte-vencido { background: #B23B2C; }
.tramo-parte-sinvto { background: #9aa7bd; }
.tramo-importe { font-size: 11px; color: #1a2744; width: 86px; text-align: right; flex-shrink: 0; }
.tramo-docs { font-size: 11px; color: #7c8db0; width: 40px; text-align: right; flex-shrink: 0; }
.leyenda { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 10px; }
.leyenda-item { font-size: 11px; color: #4a5a78; display: flex; align-items: center; gap: 5px; }
.leyenda-color { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
.leyenda-plazo { background: #0F6E56; }
.leyenda-vencido { background: #B23B2C; }
.leyenda-sinvto { background: #9aa7bd; }

/* NAVEGACIÓN */
.recorridos { display: flex; gap: 8px; margin-bottom: 8px; }
.recorrido-btn {
  font-size: 12px;
  padding: 5px 12px;
  border: 1px solid #d7dde8;
  border-radius: 999px;
  background: white;
  color: #4a5a78;
}
.recorrido-btn-activo { background: #534AB7; border-color: #534AB7; color: white; }
.migas { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin-bottom: 10px; }
.miga { font-size: 11px; border: 0; background: none; color: #185FA5; padding: 0; }
.miga-sep { font-size: 11px; color: #9aa7bd; }
/* #830 · lo que hay que hacer en el primer nivel del recorrido por antigüedad. El tamaño y el color
   son los del texto de la pantalla (13px, el de los rótulos de bloque) y no los del gris pequeño de
   `bloque-vacio`: en gris pequeño se leía como un bloque vacío. Sin `gap`, por el browserslist. */
.aviso-primer-nivel {
  background: #F2F7FD;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 4px 0;
}
.aviso-primer-nivel-texto { font-size: 13px; color: #1a2744; line-height: 1.45; margin: 0; }
.aviso-primer-nivel-texto + .aviso-primer-nivel-texto { margin-top: 6px; }
.boton-abrir-grafico {
  font-size: 12px;
  padding: 4px 12px;
  margin-left: 6px;
  border: 1px solid #185FA5;
  border-radius: 6px;
  background: white;
  color: #185FA5;
  cursor: pointer;
}
.boton-abrir-grafico:focus-visible { outline: 2px solid #185FA5; outline-offset: 2px; }
.grupos { display: flex; flex-direction: column; }
.grupo-fila {
  display: grid;
  grid-template-columns: 1fr auto 24px;
  grid-template-areas: "etiqueta importe chevron" "detalle detalle chevron";
  align-items: center;
  gap: 2px 8px;
  padding: 8px 0;
  border-bottom: 1px solid #eef1f6;
  cursor: pointer;
}
.grupo-fila:active { opacity: 0.6; }
.grupo-etiqueta { grid-area: etiqueta; font-size: 13px; color: #1a2744; font-weight: 600; }
.grupo-importe { grid-area: importe; font-size: 13px; color: #1a2744; }
.grupo-detalle { grid-area: detalle; font-size: 11px; color: #7c8db0; }
.grupo-chevron { grid-area: chevron; font-size: 16px; color: #9aa7bd; }

/* #200 · LA ESCALERA DE LA RECLAMACIÓN */
.escalon-detalle { display: block; font-size: 10px; color: #7c8db0; line-height: 1.35; margin-top: 2px; }
/* La fila que no es un escalón —«sin estado» o un valor raro— en gris, para que se vea que no
   forma parte de la escalera aunque sume en el total. */
.fila-sin-escalon td { background: #FAFBFD; color: #4a5a78; }
.etiqueta-no-escalon {
  font-size: 9px;
  border-radius: 999px;
  padding: 1px 6px;
  margin-left: 4px;
  white-space: nowrap;
  background: #EEF1F6;
  color: #4a5a78;
}
/* El pie lleva `th` para el rótulo de la fila y `td` para los totales, así que los dos se pintan
   igual aquí: el borde inferior se repite para que la celda de dato no se quede con el más claro
   de `.tabla td`. */
.tabla tfoot th, .tabla tfoot td {
  text-align: left;
  font-size: 11px;
  color: #1a2744;
  font-weight: 700;
  padding: 6px 8px;
  border-top: 1px solid #d7dde8;
  border-bottom: 1px solid #d7dde8;
  white-space: nowrap;
}

/* #189 · LAS PERSONAS */
/* El rótulo de las dos tablas de «Las personas» lo pinta ahora su `BloquePlegable` (#749), que trae
   su propio tamaño de subtítulo. Este rótulo lo siguen usando los cuatro apartados de «La calidad
   del dato» (#201), que no se plegan uno a uno: sin él saldrían como texto corrido. */
.bloque-subtitulo { font-size: 12px; font-weight: 600; color: #4a5a78; margin: 0 0 6px; }
/* Y la separación entre las dos tablas, que no es del rótulo sino del hueco que hay entre ellas. */
.bloque-subtitulo-separado { margin-top: 18px; }
/* La fila de aviso: es la fila de la que hay que bajar, así que se ve antes de leerla. Mismo ámbar
   que el resto de los avisos de «esto está incompleto» de la pantalla. */
.fila-aviso th, .fila-aviso td { background: #FDF4E3; color: #6B4A10; font-weight: 700; }
.marca-aviso, .marca-fuera, .marca-edad {
  font-size: 9px;
  border-radius: 999px;
  padding: 1px 6px;
  margin-left: 4px;
  white-space: nowrap;
  font-weight: 400;
}
.marca-aviso { background: #F7E3BF; color: #6B4A10; }
.marca-fuera { background: #EEF1F6; color: #4a5a78; }
.marca-edad { background: #FDECEA; color: #8E2B20; }
/* El username debajo del nombre: hace falta para saber a quién se le asigna en la bandeja, porque
   lo que se escribe en el PGC es el username y no el nombre. */
.gestor-username { display: block; font-size: 10px; color: #7c8db0; font-weight: 400; }
/* La fila del dato que falta —«sin empleado de ventas»— en gris: suma en el total, pero no es una
   persona. Mismo tratamiento que la fila «sin estado» de la escalera. */
.fila-sin-dato th, .fila-sin-dato td { background: #FAFBFD; color: #4a5a78; }
.celda-nota { display: block; font-size: 10px; color: #7c8db0; font-weight: 400; }
/* Las dos tablas llevan `th` de fila, así que la celda de cabecera se pinta como una celda de dato
   en vez de como una cabecera de columna: lo que rotula es la fila, no la columna. */
.tabla tbody th {
  text-align: left;
  font-weight: 600;
  color: #1a2744;
  padding: 6px 8px;
  border-bottom: 1px solid #eef1f6;
  white-space: nowrap;
}

/* TABLA */
.tabla-scroll { overflow-x: auto; }
.tabla { width: 100%; border-collapse: collapse; font-size: 11px; }
.tabla th {
  text-align: left;
  color: #7c8db0;
  font-weight: 600;
  padding: 6px 8px;
  border-bottom: 1px solid #d7dde8;
  white-space: nowrap;
}
.tabla td {
  padding: 6px 8px;
  border-bottom: 1px solid #eef1f6;
  color: #1a2744;
  white-space: nowrap;
}
.tabla .num { text-align: right; }
/* #201 · la celda de texto largo —«dónde está el dato», «por qué no se atribuye»— tiene que poder
   partirse: `.tabla td` lleva `nowrap` porque las demás columnas son códigos y cifras, y una frase
   de dos líneas con `nowrap` empuja el ancho de la tabla hasta que el scroll horizontal se come la
   primera columna. El ancho máximo es para que no se estire en una pantalla grande. */
.celda-envuelve { white-space: normal; max-width: 280px; line-height: 1.35; }
.num-alerta { color: #B23B2C; font-weight: 600; }
.celda-falta { color: #9aa7bd; font-style: italic; }
.fila-abono td { background: #FBF7F1; }
.etiqueta-abono, .marca-gestionada, .marca-sin-gestionar {
  font-size: 9px;
  border-radius: 999px;
  padding: 1px 6px;
  margin-left: 4px;
  white-space: nowrap;
}
.etiqueta-abono { background: #F3E6D4; color: #8A5B12; }
.marca-gestionada { background: #E1F5EE; color: #0F6E56; }
.marca-sin-gestionar { background: #FDECEA; color: #8E2B20; }
.boton-gestiones {
  font-size: 11px;
  padding: 3px 8px;
  border: 1px solid #d7dde8;
  border-radius: 6px;
  background: white;
  color: #185FA5;
}

/* POPUP DE GESTIONES */
.gestion-fila { border-bottom: 1px solid #eef1f6; padding: 8px 0; }
.gestion-cabecera { margin: 0; font-size: 13px; color: #1a2744; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.gestion-estado { font-size: 11px; color: #4a5a78; }
.gestion-cuenta { font-size: 10px; background: #E1F5EE; color: #0F6E56; border-radius: 999px; padding: 1px 8px; }
.gestion-nocuenta { font-size: 10px; background: #F2F5FA; color: #4a5a78; border-radius: 999px; padding: 1px 8px; }
.gestion-detalle { margin: 3px 0 0; font-size: 12px; color: #4a5a78; }
.gestion-origen { margin: 3px 0 0; font-size: 10px; color: #9aa7bd; }
</style>

<script>
import SelloDatos from '../../components/SelloDatos.vue';
import CuadroComportamientoPago from '../../components/CuadroComportamientoPago.vue';
import BloquePlegable from '../../components/BloquePlegable.vue';
import CuadroGestionCobro from '../../components/CuadroGestionCobro.vue';
import FactCobro from '../../js/db/FactCobro.js';
import Utils from '../../js/Utils.js';
import carrilPeticiones from '../../js/utils/carrilPeticiones.js';
import plegadoDeBloques from '../../js/utils/plegadoDeBloques.js';
import buscarPersonas from '../../js/utils/buscarPersonas.js';

const NOMBRES_MES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto',
  'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/**
 * Los dos recorridos que pidió dirección, con sus niveles en orden. Al acabarse los niveles se
 * enseña la tabla de facturas, y de una factura se abren sus tareas de gestión de cobro.
 *
 * `TRAMO` no tiene lista propia: su nivel ES el gráfico de antigüedad, que ya enseña las mismas
 * cifras. Dos listas con los mismos números acaban divergiendo, y aquí además la de arriba es la
 * que dirección pidió ver.
 */
const NIVELES = {
  ANTIGUEDAD: ['TRAMO', 'EMPLEADO', 'CLIENTE'],
  GESTOR: ['GESTOR', 'CLIENTE']
};

/** De qué campo del filtro de la API es cada nivel, y cómo se rotula su lista. */
const DIMENSIONES = {
  TRAMO: { campo: 'tramos', titulo: 'Tramos de antigüedad' },
  EMPLEADO: { campo: 'empleadosVentas', titulo: 'Empleados de ventas' },
  GESTOR: { campo: 'gestoresCobro', titulo: 'Gestores de cobro' },
  CLIENTE: { campo: 'clientes', titulo: 'Clientes' }
};

/**
 * #749 · **Los bloques plegables de esta pantalla, y cuál nace abierto.**
 *
 * El estado inicial es el que pidió dirección el 20/10/2026: *«abiertas las cifras clave y cerrado
 * lo secundario»*. Abiertos, entonces, los dos grupos de cifras —los titulares y las dos cifras del
 * pendiente—, el gráfico de antigüedad (que además es el primer nivel de la navegación: cerrado de
 * partida, el recorrido nacería señalando un bloque que no se ve) y el recorrido con su tabla.
 * Cerrados los tres informes de solo lectura que los filtros no recortan, y el panel de filtros,
 * que ya nacía cerrado antes de esto.
 *
 * Esta lista es además la que gobierna lo que se recuerda: `plegadoDeBloques` solo devuelve las
 * claves que están aquí, así que un bloque que se quite de la pantalla deja de recordarse y no
 * resucita con el apunte que quedara en el navegador.
 *
 * «La calidad del dato» (#201) entró en `develop` después que el resto y nace cerrada por lo mismo
 * que la escalera y las personas: es otro informe de solo lectura que los filtros no recortan.
 *
 * «La gestión» (#199) entró también después y se le puso el triángulo en la #830. Nace cerrada por
 * el mismo criterio: es un informe de solo lectura que los filtros no recortan, como el cuadro de
 * comportamiento que va justo encima. Cerrada dice sus dos cifras clave —facturas sin ninguna
 * gestión y silencios—, así que cerrarla no esconde la noticia.
 */
const BLOQUES = {
  titulares: true,
  dosCifras: true,
  escalera: false,
  personas: false,
  personasGestores: true,
  personasVentas: true,
  calidad: false,
  comportamiento: false,
  gestion: false,
  filtros: false,
  antiguedad: true,
  navegacion: true,
  facturas: true
};

/** Cómo se llama esta pantalla en el almacén del navegador. No se enseña ni se traduce. */
const PANTALLA = 'centro-cobros';

/**
 * El registro del navegador donde se recuerda qué bloques están plegados, para quien esté
 * identificado **ahora**.
 *
 * Se pide uno cada vez en vez de guardarlo: el usuario se resuelve en el momento de leer o de
 * escribir, así que un apunte nunca se escribe con la identidad de la sesión anterior. Es el mismo
 * criterio con el que el carril de peticiones pregunta por la sesión cuando la respuesta llega y no
 * cuando se lanza. Y es barato: la fábrica solo compone la clave del almacén.
 */
function registroDePlegado() {
  const usuario = Utils.getUser();
  return plegadoDeBloques(PANTALLA, (usuario && usuario.username) || null, BLOQUES);
}

/**
 * El valor con el que se pide «los que no tienen esto».
 *
 * Cadena vacía y no `null`: la API trata el hueco de la lista como «los que no lo tienen» y un
 * `null` dentro de un array no sobrevive bien a `JSON.stringify` en todas las pasarelas. El grupo
 * «Sin gestor asignado» es hoy la cartera entera, así que este camino es el que más se va a usar.
 */
const SIN_VALOR = '';

/** Los filtros en blanco: el estado del que se parte y al que vuelve «Quitar filtros». */
function filtrosEnBlanco() {
  return {
    anioEmision: null,
    mesEmision: null,
    empleadoVentas: null,
    gestorCobro: null,
    cliente: null,
    estadoGestion: null,
    formaPago: null,
    tramo: null,
    soloVencido: false,
    // Por omisión el vencido cuenta los abonos: es la cartera completa de D-COB-11.
    vencidoIncluyeAbonos: true,
    sinGestorAsignado: false
  };
}

/** Una lista de un solo valor para la API, o nada si el filtro está en blanco. */
function lista(valor) {
  return valor === null || valor === undefined || valor === '' ? null : [valor];
}

/**
 * Lo largo que se dibuja la barra de un tramo: la suma de las magnitudes de sus tres partes (ver
 * `escalaTramos`). Una sola función para el gráfico y para su resumen plegado: con el neto, un
 * tramo de facturas y abonos casi compensados tenía la barra más larga y el resumen señalaba otro
 * como «el que más pesa» (revisión CODEX).
 */
function magnitudTramo(tramo) {
  return Math.abs(Number(tramo.importeEnPlazo) || 0)
    + Math.abs(Number(tramo.importeVencido) || 0)
    + Math.abs(Number(tramo.importeSinVencimiento) || 0);
}

export default {
  components: { SelloDatos, CuadroComportamientoPago, CuadroGestionCobro, BloquePlegable },
  props: {
    /**
     * #193 · Si esta persona puede entrar en el informe de abonos abiertos, y por tanto si se
     * dibuja su enlace.
     *
     * Llega **resuelto** desde la ruta, con el `Usuario.me()` de esta carga, y no se pregunta aquí:
     * es la misma respuesta que da la ruta del informe (`permisos-cobros.js`), así que el enlace y
     * la pantalla de destino no pueden decir cosas distintas. Mismo patrón que `puedeAbrirFicha`
     * del Mapa de aprobación (#65).
     *
     * **Por omisión `false`**: sin respuesta no se enseña el enlace. Si la ruta dejara de pasarlo,
     * el enlace desaparece —que es incómodo pero no engaña a nadie—; al revés se pintaría un botón
     * hacia un 403.
     */
    puedeVerInformeAbonos: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      /**
       * La PRIMERA carga, la única que no tiene nada que enseñar todavía. Es la que pinta el
       * preloader de la pantalla entera.
       *
       * #749 · Antes valía para cualquier carga, y de ahí el fallo que señaló dirección: con las
       * cifras ya delante, un filtro la ponía a `true` y la pantalla se quedaba en blanco y volvía
       * al principio. Una recarga con cifras delante pasa ahora por `refrescando`.
       */
      cargando: false,
      /**
       * #749 · Que se está repitiendo la lectura **con las cifras de antes en pantalla**, y QUÉ
       * puede cambiar: `'FILTROS'` si solo se ha movido un filtro o el camino bajado, `'TODO'` si
       * se ha recargado la pantalla. `null` cuando no hay nada en vuelo.
       *
       * Gobierna dónde se enseña el indicador de carga local, y por eso no es un booleano: la
       * escalera, las personas y las dos cifras se calculan sobre la cartera visible ENTERA y
       * vuelven idénticas al cambiar un filtro (ver `FactCobro.cartera`), así que avisar allí de
       * que se están actualizando sería decir que algo va a cambiar cuando no va a cambiar nada.
       */
      refrescando: null,
      /**
       * #749 · Con qué recorte salieron las cifras que hay en pantalla (ver `recorteDeLaLectura`),
       * o `''` si son de la cartera entera. Se apunta al llegar la respuesta y no se deduce de
       * `filtro`: mientras la lectura nueva está en vuelo, `filtro` ya dice lo que se acaba de pedir
       * y las cifras todavía son las de antes, y la cabecera las atribuiría a otros filtros.
       */
      recorteDeLasCifras: '',
      error: null,
      dto: null,
      filtro: filtrosEnBlanco(),
      /**
       * #749 · Qué bloques están abiertos. Lo de partida es `BLOQUES` con lo que esta persona
       * dejara apuntado en este navegador por encima.
       *
       * Las claves están **todas** desde el principio, salgan o no de lo guardado: Vue 2 no
       * observa las que se añaden después, y un bloque cuya clave naciera ausente se quedaría sin
       * repintarse al plegarlo.
       */
      plegado: registroDePlegado().inicial(),
      /**
       * Lo escrito en el buscador de los tres filtros de lista larga (CLAUDE.md §9). **No es un
       * filtro**: no viaja a la API y no recorta la cartera, solo acorta la lista del desplegable.
       */
      busca: { empleadoVentas: '', gestorCobro: '', cliente: '' },
      recorrido: 'ANTIGUEDAD',
      /**
       * Si esta persona entra en «Mi trabajo de hoy» (#190), y por tanto si se le dibuja el enlace.
       *
       * En `data` y no en un `computed`: lo contesta el servidor y el `computed` no tendría ninguna
       * dependencia reactiva que lo volviera a evaluar cuando llegue la respuesta. Arranca en
       * `false` y se queda en `false` si la pregunta falla — **fail-closed**: ante la duda no se
       * dibuja la puerta, porque un botón que lleva a un 403 hace creer que te han quitado permisos
       * (regla de pantalla de dirección, 05/10/2026).
       */
      puedeEntrarTrabajoHoy: false,
      // El camino bajado: un paso por nivel, con la clave y el rótulo que se enseña en las migas.
      ruta: [],
      nombresMes: NOMBRES_MES,
      gestiones: {
        abierto: false,
        cargando: false,
        error: null,
        titulo: '',
        lista: []
      },
      // #201 · la lista que abre una fila del bloque de calidad del dato. Estado propio y no
      // compartido con `gestiones`: son dos listas distintas de cosas distintas, y con un solo
      // objeto abrir una cerraría la otra a medio cargar.
      documentos: {
        abierto: false,
        cargando: false,
        error: null,
        titulo: '',
        lista: []
      },
      // #201 · el listado entero de los vínculos de gestión sin atribuir, que es lo que abre el pie
      // de esa tabla cuando el tope la ha recortado. Estado propio por lo mismo que el de arriba, y
      // además son otras columnas: estos no son documentos de la cartera, son tareas que no han
      // señalado a ninguno.
      vinculos: {
        abierto: false,
        cargando: false,
        error: null,
        lista: []
      }
    };
  },
  computed: {
    /**
     * Si la pantalla está enseñando cifras ahora mismo. Gobierna el sello de datos (#16): mismo
     * criterio que la Tarjeta B, una sola condición para que el estado que se añada mañana no se
     * olvide de apagarlo.
     */
    hayCifras: function() {
      return !!this.dto && !this.cargando;
    },
    tarjetas: function() {
      return (this.dto && this.dto.tarjetas) || {};
    },
    dosCifras: function() {
      return (this.dto && this.dto.dosCifras) || {};
    },
    /** Las filas de la tabla. Una sola lectura, para no repetir el resguardo en cuatro sitios. */
    filas: function() {
      return (this.dto && this.dto.facturas) || [];
    },
    /**
     * #200 · La escalera de la reclamación, con el resguardo en un solo sitio.
     *
     * Viene dentro de la respuesta de la cartera y no de una lectura propia: sale de la misma
     * pasada, así que la suma de sus filas es el total de documentos abiertos por construcción.
     */
    escalera: function() {
      return (this.dto && this.dto.escalera) || {};
    },
    escalones: function() {
      return this.escalera.escalones || [];
    },
    /**
     * #189 · Las personas, con el resguardo en un solo sitio.
     *
     * Viene dentro de la respuesta de la cartera y no de una lectura propia, igual que la escalera:
     * sale de la misma pasada, así que los totales de las dos tablas son el pendiente total de esta
     * pantalla por construcción. Y tampoco la recortan los filtros.
     */
    personas: function() {
      return (this.dto && this.dto.personas) || {};
    },
    /**
     * Las filas de gestor **sin** la de aviso: esa se pinta aparte y arriba de todo.
     *
     * El servidor ya las manda ordenadas —de más a menos cartera y las de raya al final— y aquí no
     * se reordenan: el orden es parte de lo que dice la tabla.
     */
    gestores: function() {
      return this.personas.gestores || [];
    },
    /**
     * #201 · La calidad del dato, con el resguardo en un solo sitio.
     *
     * Viene dentro de la respuesta de la cartera y no de una lectura propia, igual que la escalera
     * y las personas: sale de la misma pasada, así que el denominador de sus porcentajes es el
     * total de documentos abiertos de esta pantalla por construcción. Y tampoco la recortan los
     * filtros. Lo que sí tiene lectura propia es la lista que abre cada una de sus filas.
     */
    calidad: function() {
      return (this.dto && this.dto.calidad) || {};
    },
    /**
     * La alerta de los documentos que dicen «Cobrado total» y siguen abiertos.
     *
     * Viene **siempre**, también a cero, igual que la línea de empresa extinta (dirección,
     * 20/10/2026): la línea existiendo con su cero es lo que distingue «se ha comprobado y no hay
     * ninguna» de «no se ha comprobado», y en un bloque que está para decir lo que falta (D-COB-8)
     * ese silencio era el mismo defecto que una cifra sin su lista.
     *
     * Sigue devolviendo `null` si el campo no llega, y eso NO es el caso de arriba: con una API
     * anterior a este cambio no hay nada que pintar —ni cifra ni «ninguna»— y escribir «ninguna»
     * sin que nadie lo haya comprobado sería afirmar sin dato. Los dos PRs se mezclan con la API
     * delante.
     */
    cobradoTotal: function() {
      return this.calidad.cobradoTotalAbierta || null;
    },
    /**
     * Si esa alerta ha encontrado algo, que es lo que decide qué se pinta: la tabla con las
     * facturas nombradas, o la línea de «ninguna».
     *
     * Por `documentos` —el recuento del servidor sobre todos— y no por el tamaño del listado, que
     * va con tope: con 60 documentos recortados a 50, el listado nunca está vacío, pero si algún
     * día lo estuviera con documentos contados, pintar «ninguna» encima de una cifra que dice 60
     * sería que dos partes de la misma línea se contradijeran.
     */
    hayCobradoTotal: function() {
      return !!(this.cobradoTotal && this.cobradoTotal.documentos);
    },
    /**
     * La línea de empresa extinta. Esta sí viene **siempre**, también a cero (criterio 5): es lo
     * que explica por qué el pendiente gestionable puede ser menor que el total, así que la
     * pantalla la pinta aunque no haya ningún documento.
     */
    extinta: function() {
      return this.calidad.empresaExtinta || null;
    },
    /** Los vínculos de gestión sin atribuir (D-COB-12), con el resguardo en un solo sitio. */
    gestionesSinAtribuir: function() {
      return this.calidad.gestionesSinAtribuir || [];
    },
    /**
     * Si esa comprobación ha encontrado algo. Por la cifra del servidor y no por las filas que han
     * cabido, por lo mismo que `hayCobradoTotal`: el tope recorta la tabla y no el recuento.
     */
    haySinAtribuir: function() {
      return this.vinculosSinAtribuir > 0;
    },
    /**
     * Cuántos vínculos sin atribuir hay EN TOTAL, que es lo que dice la cabecera del bloque: la
     * cifra del servidor, no las filas que han cabido en el tope.
     *
     * Con una API antigua que no mande el campo, la suma de las que hay y las que no caben, que es
     * la misma cifra: así la cabecera no escribe «undefined» mientras los dos PRs no estén
     * mezclados en orden.
     */
    vinculosSinAtribuir: function() {
      const total = this.calidad.gestionesSinAtribuirVinculos;
      return total === null || total === undefined
        ? this.gestionesSinAtribuir.length + (this.calidad.gestionesSinAtribuirOmitidas || 0)
        : total;
    },
    /** Si algún documento se queda sin clasificar por no tener fecha de vencimiento. */
    haySinVencimiento: function() {
      return !!(this.dto && this.dto.tarjetas && this.dto.tarjetas.documentosSinVencimiento);
    },
    /**
     * Si se puede decir sobre cuántos documentos está hecha la edad media.
     *
     * Se comprueba que el recuento **venga**, no que sea distinto de cero: un cero ahí es un dato
     * —«no hay ningún documento con saldo positivo, de ahí el guion de arriba»— y es justo el caso
     * en el que hace falta decirlo. Lo que no se puede escribir es un hueco: una API antigua que no
     * manda el campo pondría «sobre undefined de 1.422».
     */
    hayRecuentoEdadMedia: function() {
      const recuento = this.tarjetas.documentosEdadMedia;
      return recuento !== null && recuento !== undefined && recuento !== '';
    },
    /**
     * La escala del gráfico: la barra más larga que hay que dibujar.
     *
     * **Común a todas las filas**, que es lo que dirección pidió: con una escala por fila, un tramo
     * de 2.900 € dibujaría la misma barra que uno de 147.907 € y el gráfico diría lo contrario de
     * lo que pasa.
     *
     * Y es la suma de las MAGNITUDES de las tres partes, no el importe neto del tramo. Un tramo
     * puede mezclar signos —facturas en plazo en positivo y abonos vencidos en negativo—, y cada
     * parte se dibuja en valor absoluto. Escalando con el neto, un tramo de +100 en plazo y −60
     * vencido tendría un neto de 40 y pintaría 250 % + 150 %: Flexbox lo comprime y la proporción
     * entre «en plazo» y «vencido» sale falseada. Con la suma de magnitudes, las partes de una fila
     * nunca pasan del 100 %, porque |a+b+c| ≤ |a|+|b|+|c| y por tanto esta escala nunca es menor
     * que el neto.
     *
     * La barra mide magnitud; el signo se lee en el importe, que se escribe al lado tal cual.
     */
    escalaTramos: function() {
      const tramos = (this.dto && this.dto.tramos) || [];
      let maximo = 0;
      tramos.forEach(function(tramo) {
        maximo = Math.max(maximo, magnitudTramo(tramo));
      });
      return maximo;
    },
    /** La dimensión del nivel en el que estamos, o `null` si ya estamos en la tabla. */
    dimensionActual: function() {
      const niveles = NIVELES[this.recorrido];
      return this.ruta.length < niveles.length ? niveles[this.ruta.length] : null;
    },
    enLaTabla: function() {
      return this.dimensionActual === null;
    },
    /**
     * Si el nivel actual **es** el gráfico de antigüedad, o sea `TRAMO`.
     *
     * Ese nivel no tiene lista propia (ver `NIVELES`), así que el bloque de grupos no se dibuja: si
     * se dibujara, en la carga inicial saldría «no hay nada que enseñar en este nivel» justo debajo
     * de un gráfico lleno de cifras, contradiciendo lo que se está viendo.
     */
    nivelEsElGrafico: function() {
      return this.dimensionActual === 'TRAMO';
    },
    /**
     * #830 · Si el gráfico de antigüedad no tiene ningún documento en ningún tramo. Con la misma
     * cuenta que el «No hay datos» de su cabecera plegada (`resumenAntiguedad`): el recorrido no
     * puede mandar elegir un tramo mientras el gráfico dice que no hay nada.
     */
    graficoSinDocumentos: function() {
      const tramos = (this.dto && this.dto.tramos) || [];
      return !tramos.some(function(tramo) { return (Number(tramo.documentos) || 0) > 0; });
    },
    /**
     * Si pinchar una barra del gráfico baja de nivel: solo cuando el gráfico ES el nivel actual.
     * En los demás niveles el gráfico sigue a la vista —refleja los filtros puestos— pero no
     * navega, porque bajar dos veces al mismo tramo no significa nada.
     *
     * #830 · Y tampoco con el gráfico sin documentos: el recorrido dice entonces que no hay tramo
     * que elegir, y la cabecera no puede decir a la vez «pincha un tramo».
     */
    tramoPinchable: function() {
      return this.nivelEsElGrafico && !this.graficoSinDocumentos;
    },
    tituloNivel: function() {
      const dimension = DIMENSIONES[this.dimensionActual];
      return dimension ? dimension.titulo : '';
    },
    /** Los grupos del nivel actual. El de TRAMO no pasa por aquí: su nivel es el gráfico. */
    listaNivel: function() {
      if (!this.dto) return [];
      if (this.dimensionActual === 'EMPLEADO') return this.dto.porEmpleadoVentas || [];
      if (this.dimensionActual === 'GESTOR') return this.dto.porGestorCobro || [];
      if (this.dimensionActual === 'CLIENTE') return this.dto.porCliente || [];
      return [];
    },
    /**
     * Qué filtros hay puestos, para poder leerlo con el bloque plegado.
     *
     * Con el **rótulo** de cada opción y no con su clave (#749): la clave de un cliente es su
     * código de la consulta de Facturas, y «cliente: 43001» con el bloque cerrado no dice qué está
     * filtrado. Si la opción ya no está en la lista que manda el servidor se escribe la clave tal
     * cual: es menos legible, pero callarla esconde un filtro que sí está recortando la cartera.
     *
     * Y el criterio del vencido va **dentro** de «solo vencido», porque son el mismo filtro: con
     * «Solo facturas» elegido, el resumen decía «solo vencido» a secas y el bloque cerrado no
     * dejaba ver con cuál de los dos criterios se estaba recortando.
     */
    resumenFiltros: function() {
      const puestos = [];
      if (this.filtro.anioEmision) {
        puestos.push(this.filtro.mesEmision
          ? NOMBRES_MES[this.filtro.mesEmision - 1] + ' de ' + this.filtro.anioEmision
          : String(this.filtro.anioEmision));
      }
      if (this.filtro.empleadoVentas) {
        puestos.push('ventas: ' + this.etiquetaDeOpcion('opcionesEmpleadoVentas', this.filtro.empleadoVentas));
      }
      if (this.filtro.gestorCobro) {
        puestos.push('gestor: ' + this.etiquetaDeOpcion('opcionesGestorCobro', this.filtro.gestorCobro));
      }
      if (this.filtro.cliente) {
        puestos.push('cliente: ' + this.etiquetaDeOpcion('opcionesCliente', this.filtro.cliente));
      }
      if (this.filtro.estadoGestion) {
        puestos.push(this.etiquetaDeOpcion('opcionesEstadoGestion', this.filtro.estadoGestion));
      }
      if (this.filtro.formaPago) {
        puestos.push(this.etiquetaDeOpcion('opcionesFormaPago', this.filtro.formaPago));
      }
      if (this.filtro.tramo) {
        puestos.push(this.etiquetaDeOpcion('opcionesTramo', this.filtro.tramo));
      }
      if (this.filtro.soloVencido) {
        puestos.push('solo vencido ('
          + (this.filtro.vencidoIncluyeAbonos ? 'con abonos' : 'solo facturas') + ')');
      }
      if (this.filtro.sinGestorAsignado) puestos.push('sin gestor');
      return puestos.length ? puestos.join(' · ') : 'ninguno';
    },

    // =============================================================== #749 · los tres desplegables
    //                                                      de lista larga, buscables escribiendo
    //
    // Las tres listas salen de la cartera visible entera: la de clientes trae cientos de entradas y
    // la de empleados, decenas. La regla permanente de dirección (CLAUDE.md §9, 18/10/2026) es que
    // una lista larga se tiene que poder buscar escribiendo, y la búsqueda es la MISMA que la del
    // resto del ERP (`buscarPersonas`): sin tildes, sin mayúsculas, palabra a palabra y por trozos,
    // mirando el rótulo y el código.
    opcionesVentasBuscadas: function() {
      return this.opcionesBuscadas('opcionesEmpleadoVentas', this.busca.empleadoVentas,
        this.filtro.empleadoVentas);
    },
    opcionesGestorBuscadas: function() {
      return this.opcionesBuscadas('opcionesGestorCobro', this.busca.gestorCobro,
        this.filtro.gestorCobro);
    },
    opcionesClienteBuscadas: function() {
      return this.opcionesBuscadas('opcionesCliente', this.busca.cliente, this.filtro.cliente);
    },

    // ======================================================= #749 · lo esencial de cada bloque,
    //                                                   para que cerrado no se quede mudo
    //
    // Cada resumen se compone con LOS MISMOS campos que pinta su bloque por dentro, y ninguno suma
    // nada: los totales los manda el servidor. Es la misma razón por la que el pie de la escalera no
    // los calcula aquí —una pantalla que suma lo que quiere no comprueba nada— y además la única
    // manera de que la cabecera cerrada no pueda decir lo contrario que el bloque abierto.
    /** Las dos cifras grandes de las tarjetas: el pendiente total y el vencido con abonos. */
    resumenTitulares: function() {
      const cifras = 'Pendiente total ' + this.formatEuros(this.tarjetas.pendienteTotal)
        + ' € · vencido ' + this.formatEuros(this.tarjetas.vencidoConAbonos) + ' €';
      // Las tarjetas obedecen a los filtros y «Las dos cifras del pendiente», justo debajo, no: con
      // los dos bloques cerrados, «Pendiente total 0 €» encima de «Cartera completa 735.418 €» se
      // leía como una contradicción aunque las dos cifras fueran ciertas (JJCS, 20/10/2026).
      const recorte = this.recorteDeLasCifras;
      return recorte ? recorte.charAt(0).toUpperCase() + recorte.slice(1) + ' · ' + cifras : cifras;
    },
    resumenDosCifras: function() {
      return 'Cartera completa ' + this.formatEuros(this.dosCifras.cartera) + ' €';
    },
    resumenEscalera: function() {
      return this.numero(this.escalera.documentos) + ' documentos abiertos · '
        + this.formatEuros(this.escalera.importeTotal) + ' €';
    },
    /**
     * El pendiente gestionable sobre el total, que es la cifra que el bloque existe para explicar, y
     * las dos comprobaciones que avisan en rojo solo si han encontrado algo: cerrado, el bloque no
     * puede esconder una alerta que abierto se lee lo primero.
     */
    resumenCalidad: function() {
      const calidad = this.calidad;
      let resumen = 'Pendiente gestionable ' + this.formatEuros(calidad.pendienteGestionable)
        + ' € de ' + this.formatEuros(calidad.importeTotal) + ' €';
      if (this.hayCobradoTotal) {
        resumen += ' · ' + this.numero(this.cobradoTotal.documentos)
          + ' abiertos con «Cobrado total»';
      }
      if (this.haySinAtribuir) {
        resumen += ' · ' + this.numero(calidad.gestionesSinAtribuirDistintas)
          + ' gestiones sin atribuir';
      }
      return resumen;
    },
    resumenPersonas: function() {
      return this.numero(this.personas.documentos) + ' documentos · '
        + this.formatEuros(this.personas.importeTotal) + ' €';
    },
    /** De la tabla por gestor, lo que es su titular: la cartera que no tiene dueño. */
    resumenGestores: function() {
      const sinAsignar = this.personas.sinAsignar;
      if (!sinAsignar) return this.gestores.length + ' gestores';
      return 'Sin asignar ' + this.formatEuros(sinAsignar.carteraAsignada) + ' € · '
        + this.gestores.length + ' gestores';
    },
    /**
     * La fila `sinEmpleado` no es una persona —la tabla la rotula «no es de nadie»—, así que no
     * cuenta como empleado: con ella dentro, una cartera sin ningún comercial decía «1 empleado de
     * ventas». Se dice aparte, como «Sin asignar» en el resumen de gestores.
     */
    resumenVentas: function() {
      const filas = this.personas.ventas || [];
      const sinEmpleado = filas.find(function(fila) { return fila.sinEmpleado; });
      const cuantos = filas.length - (sinEmpleado ? 1 : 0);
      const empleados = cuantos === 1 ? '1 empleado de ventas' : cuantos + ' empleados de ventas';
      if (!sinEmpleado) return empleados;
      return empleados + ' · sin empleado ' + this.formatEuros(sinEmpleado.pendiente) + ' €';
    },
    /**
     * Del gráfico, el tramo que más pesa. No la suma de los tramos: esa cifra ya la da la tarjeta
     * «Pendiente total», y calcularla aquí sería una segunda cuenta de lo mismo que acabaría
     * diciendo otra cosa el día que el servidor cambie lo que entra en cada tramo.
     *
     * «Pesa» es lo que pesa en el gráfico: la barra más larga, medida con `magnitudTramo` igual que
     * la dibuja `escalaTramos`. El importe que se dice es el neto, el mismo que el gráfico escribe
     * al lado de esa barra.
     */
    resumenAntiguedad: function() {
      const tramos = (this.dto && this.dto.tramos) || [];
      let mayor = null;
      let pesoMayor = 0;
      let documentos = 0;
      tramos.forEach(function(tramo) {
        documentos += Number(tramo.documentos) || 0;
        const peso = magnitudTramo(tramo);
        if (peso > pesoMayor) {
          mayor = tramo;
          pesoMayor = peso;
        }
      });
      // Sin ninguna barra dibujada no hay un tramo que pese más que otro: «el tramo que más pesa:
      // 0-30 días · 0 €» señalaba el primero de la lista como si fuera un dato (JJCS, 20/10/2026).
      // Y se mira la barra, no el neto: un tramo de +100 y −100 suma 0 € pero se dibuja, y decir
      // «todos los tramos a 0 €» encima de esa barra contradecía al gráfico.
      if (!mayor) {
        if (documentos === 0) {
          return this.recorteDeLasCifras ? 'No hay datos ' + this.recorteDeLasCifras : 'No hay datos';
        }
        return 'Todos los tramos a 0 € · ' + this.numero(documentos) + ' documentos';
      }
      return 'El tramo que más pesa: ' + mayor.etiqueta + ' · '
        + this.formatEuros(mayor.importeTotal) + ' €';
    },
    /** Del recorrido, dónde está: el recorrido elegido y el camino bajado. */
    resumenNavegacion: function() {
      const recorrido = this.recorrido === 'GESTOR' ? 'Por gestor de cobro' : 'Por antigüedad';
      const camino = this.ruta.map(function(paso) { return paso.etiqueta; });
      return recorrido + ' · ' + (camino.length ? camino.join(' › ') : 'Todo');
    },
    resumenFacturas: function() {
      const cuantas = this.filas.length;
      const enLaTabla = cuantas === 1 ? '1 factura' : cuantas + ' facturas';
      // Las omitidas se dicen también aquí: cerrado, «120 facturas» a secas se leería como que son
      // todas las que hay, y el aviso que lo desmiente está dentro del bloque.
      const omitidas = this.dto && this.dto.filasOmitidas;
      return omitidas ? enLaTabla + ' · ' + omitidas + ' no caben' : enLaTabla;
    }
  },
  /**
   * Los carriles de peticiones (uno por carga) se crean aquí y no en `data`: no son estado de
   * pantalla y meterlos en `data` los volvería reactivos para nada.
   *
   * Uno por carga y no uno solo porque son cargas independientes: abrir las gestiones de una
   * factura no tiene que descartar la carga de la cartera que pudiera estar en vuelo, ni al revés.
   *
   * **Y no queda ninguna petición de esta pantalla fuera de un carril**, tampoco la del acceso a
   * «Mi trabajo de hoy», que es la que se escribió sin él: el carril es el único sitio que decide
   * qué respuesta puede escribir en la pantalla, y una petición que se lo salta vuelve a abrir esa
   * decisión en su `then` (revisión CODEX, ronda 3).
   */
  created: function() {
    const self = this;
    this.carrilCartera = carrilPeticiones();
    // #472 · El «cargando» de cada popup lo enciende y lo apaga su carril, que es el único que sabe
    // cuándo ha contestado la petición vigente: así no hay un `false` por manejador que olvidar.
    this.carrilGestiones = carrilPeticiones({
      cargando: function(si) { self.gestiones.cargando = si; }
    });
    // Y otro para la lista de una fila de la calidad del dato (#201), por lo mismo que el de las
    // gestiones: es una carga independiente, y pinchar dos filas seguidas no tiene que dejar en
    // pantalla la respuesta de la primera si llega después.
    this.carrilDocumentos = carrilPeticiones({
      cargando: function(si) { self.documentos.cargando = si; }
    });
    // Y otro para el listado entero de los vínculos sin atribuir (#201), por lo mismo.
    this.carrilVinculos = carrilPeticiones({
      cargando: function(si) { self.vinculos.cargando = si; }
    });
    this.carrilAcceso = carrilPeticiones();
    this.resolverTrabajoHoy();
  },
  methods: {
    onPageInit: function() {
      this.cargar();
    },
    /**
     * Pregunta al servidor si esta persona entra en «Mi trabajo de hoy» (#190).
     *
     * **La misma pregunta que hace la barrera de esa ruta**, y a propósito: con dos criterios
     * —uno aquí y otro allí— esta pantalla podría dibujar un enlace que la otra rechaza, que es
     * exactamente lo que la regla de dirección del 05/10/2026 prohíbe. Y no se puede deducir de los
     * roles que haya a mano: entra cada **gestor de cobro**, y eso es quien figura como PGC en
     * alguna factura (D-COB-3), un dato que el navegador no conoce.
     *
     * Fuera de `cargar()`: no depende de los filtros, así que no se vuelve a preguntar al bajar un
     * nivel ni al cambiar un filtro —serían diez preguntas con la misma respuesta—. Donde sí se
     * vuelve a preguntar es al **recargar** (ver `recargar`). Si falla, el enlace no se dibuja y no
     * se dice nada: no es una funcionalidad que esta pantalla prometa, y un aviso de que no se ha
     * podido comprobar un enlace que esa persona quizá no tiene sería ruido.
     *
     * **Por su carril**, que es lo que faltaba. Hay dos preguntas que pueden coincidir —la de
     * `created` y la de recargar, o dos recargas seguidas— y las respuestas no vuelven en orden:
     * escribiendo las dos directamente, la vieja podía tapar un acceso recién concedido o volver a
     * dibujar el enlace después de que al gestor le retiraran su última factura, o sea llevarlo
     * justo al 403 que esta comprobación existe para evitar (revisión CODEX, ronda 3). El carril es
     * quien decide que solo escribe la última lanzada.
     *
     * El `limpiar` es el fail-closed: si la pregunta vigente falla, la puerta se cierra. Y va ahí y
     * no en un `fallo` porque el carril llama a `limpiar` **solo** cuando la que falla es la que
     * decidía; el fallo de una respuesta ya descartada no tiene que cerrar nada.
     */
    resolverTrabajoHoy: function() {
      const self = this;
      self.carrilAcceso.lanzar(FactCobro.accesoTrabajoHoy(), {
        exito: function(acceso) {
          self.puedeEntrarTrabajoHoy = !!(acceso && acceso.puedeEntrar === true);
        },
        limpiar: function() {
          self.puedeEntrarTrabajoHoy = false;
        }
      });
    },
    /**
     * El botón de recargar: la cartera **y** la puerta de «Mi trabajo de hoy».
     *
     * Quién entra ahí no es un rol: es quien figura como PGC en alguna factura (D-COB-3), así que
     * **cambia cuando otra persona reparte cartera en la bandeja de asignación**, que es algo que
     * pasa mientras esta pantalla sigue abierta. Preguntándolo solo al montar, el enlace se quedaba
     * dibujado para quien acaba de perder su última factura —y lleva a un 403, que es lo que la
     * regla de dirección del 05/10/2026 prohíbe— o escondido para quien acaba de recibir cartera.
     * Recargar es el gesto con el que se pide el estado de ahora, así que las dos preguntas van
     * juntas.
     */
    recargar: function() {
      this.resolverTrabajoHoy();
      this.cargar();
    },
    /**
     * El botón de recargar de la cabecera: la cartera, la puerta de «Mi trabajo de hoy» **y** el
     * cuadro de comportamiento (#202).
     *
     * Son dos lecturas distintas y a propósito —el cuadro mide las facturas del periodo, abiertas
     * y cerradas, que es otro conjunto—, así que recargar la pantalla tiene que pedir las
     * dos. Lo que NO hace es devolver el cuadro a su periodo por omisión: su `cargar` repite la
     * consulta con el periodo que tenga elegido, que es lo que pide que los dos selectores y las
     * cifras sigan diciendo lo mismo.
     *
     * El cuadro está **siempre** montado —solo se esconde, ver su comentario en la plantilla—, así
     * que el `ref` está ahí también cuando la cartera ha fallado, que es cuando hace falta: quien
     * pulsa aquí después de un fallo quiere las dos cosas. La comprobación se queda de todas formas
     * porque este método se puede llamar antes de que la plantilla haya montado nada.
     *
     * La puerta de «Mi trabajo de hoy» va dentro porque entra por `recargar` (#190): quien pulsa
     * aquí pide el estado de ahora, y ese acceso cambia mientras la pantalla sigue abierta.
     */
    recargarTodo: function() {
      // Sin alcance: esta lectura puede cambiar la pantalla entera, así que el indicador de carga
      // sale en todos los bloques (ver `cargar`, al que llega por `recargar`).
      this.recargar();
      const cuadro = this.$refs.cuadroComportamiento;
      if (cuadro && cuadro.cargar) {
        cuadro.cargar();
      }
      // #199 · «La gestión» tiene también su propia lectura, y por el mismo motivo se recarga aquí.
      const gestion = this.$refs.cuadroGestion;
      if (gestion && gestion.cargar) {
        gestion.cargar();
      }
    },
    /**
     * #749 · Plegar o desplegar un bloque, y apuntarlo para la próxima vez.
     *
     * **Solo toca `plegado`.** No vuelve a pedir nada, no cambia ningún filtro y no tira el camino
     * bajado: cerrar un bloque lo oculta y nada más, que es la primera de las reglas que dirección
     * fijó el 20/10/2026. Por eso el cuerpo de los bloques va con `v-show` y no con `v-if`.
     *
     * Una clave que no esté en `BLOQUES` no se apunta: sería estado que la pantalla no declara y
     * que `plegadoDeBloques` no devolvería al recargar, así que el bloque se comportaría distinto
     * antes y después de recargar la página.
     */
    alternarBloque: function(bloque) {
      if (!Object.prototype.hasOwnProperty.call(BLOQUES, bloque)) return;
      this.plegado[bloque] = !this.plegado[bloque];
      registroDePlegado().apuntar(bloque, this.plegado[bloque]);
    },
    /**
     * #830 · El botón «Abrir el gráfico» del recorrido por antigüedad: despliega el gráfico, que es
     * su primer nivel, y lo trae a la vista.
     *
     * Abre y no alterna: es un botón de abrir, y una segunda pulsación no puede cerrar lo que se
     * acaba de abrir. Lo apunta igual que el triángulo, así que al recargar la página el gráfico
     * sigue abierto.
     *
     * Lo de traerlo a la vista es porque el gráfico está ENCIMA del recorrido: al desplegarse, el
     * navegador puede mantener quieto lo que se estaba mirando y dejar las barras recién abiertas
     * por encima del borde de la pantalla, y la persona pulsaría sin ver que ha pasado algo.
     * `nearest` mueve lo justo para que se vea, sin saltar al principio de la pantalla.
     */
    abrirGraficoAntiguedad: function() {
      if (!this.plegado.antiguedad) this.alternarBloque('antiguedad');
      const self = this;
      this.$nextTick(function() {
        const bloque = self.$refs.bloqueAntiguedad;
        const el = bloque && bloque.$el;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    },
    /**
     * Pide la pantalla entera con los filtros y el camino bajado.
     *
     * Por el carril de peticiones: las flechas de los filtros y el botón de recargar pueden lanzar
     * varias seguidas y las respuestas no vuelven en orden. Sin esto, la respuesta vieja llega
     * después y pinta los datos de otro filtro encima de los buenos.
     *
     * #749 · `alcance` dice **qué puede cambiar esta lectura**, y con eso se decide en qué bloques
     * sale el indicador de carga:
     *
     *  - `'FILTROS'` · solo se ha movido un filtro, el criterio del vencido o el camino bajado. Lo
     *    que cambia son las tarjetas, el gráfico, los grupos y la tabla; la escalera, las personas
     *    y las dos cifras se calculan en el servidor sobre la cartera visible **entera** y vuelven
     *    idénticas, así que avisar allí sería anunciar un cambio que no va a pasar.
     *  - `'TODO'` · se recarga la pantalla y puede cambiar cualquier cifra.
     *
     * **Por omisión, `'TODO'`.** Un llamador nuevo que se olvide de declararlo enseña el indicador
     * en bloques que a lo mejor no cambian —molesta— en vez de callarlo en bloques que sí cambian,
     * que sería mentir.
     */
    cargar: function(alcance) {
      const self = this;
      const recorte = self.recorteDeLaLectura();
      // #749 · CON CIFRAS DELANTE, LA PANTALLA NO SE APAGA. Lo que se está pidiendo es la misma
      // pantalla con otro recorte, así que se queda como está —con su scroll y con sus cifras— y lo
      // único que cambia es que los bloques que esta lectura puede mover avisan de que se están
      // actualizando. Apagarla entera era el fallo de producción que señaló dirección: la pantalla
      // en blanco y la vuelta al principio con cada filtro.
      if (self.dto) {
        self.refrescando = alcance === 'FILTROS' ? 'FILTROS' : 'TODO';
      } else {
        self.cargando = true;
      }
      self.error = null;
      self.carrilCartera.lanzar(FactCobro.cartera(self.filtroParaLaApi()), {
        exito: function(respuesta) {
          self.cargando = false;
          self.refrescando = null;
          self.dto = respuesta;
          self.recorteDeLasCifras = recorte;
        },
        limpiar: function() {
          self.cargando = false;
          self.refrescando = null;
          // Sin cifras no se deja lo de antes en pantalla: el sello de datos y las migas estarían
          // afirmando algo sobre unos datos que esta pantalla ya no está enseñando.
          self.dto = null;
          self.recorteDeLasCifras = '';
        },
        fallo: function(e) {
          self.error = e && e.status === 403
            ? 'No tienes acceso al Centro de Cobros.'
            : 'No se ha podido cargar la cartera. Vuelve a intentarlo con el botón de recargar.';
        }
      });
    },
    /**
     * Los filtros como los espera la API: una lista por campo, y el camino bajado encima.
     *
     * El camino va **después** de los filtros del formulario y los sobreescribe: si alguien baja al
     * tramo 61-90 teniendo puesto el filtro de tramo 0-30, lo que manda es dónde ha bajado. Al
     * revés, el filtro ganaría y la pantalla enseñaría un nivel que no se corresponde con la miga.
     */
    filtroParaLaApi: function() {
      const peticion = {
        anioEmision: this.filtro.anioEmision,
        mesEmision: this.filtro.anioEmision ? this.filtro.mesEmision : null,
        empleadosVentas: lista(this.filtro.empleadoVentas),
        gestoresCobro: lista(this.filtro.gestorCobro),
        clientes: lista(this.filtro.cliente),
        estadosGestion: lista(this.filtro.estadoGestion),
        formasPago: lista(this.filtro.formaPago),
        tramos: lista(this.filtro.tramo),
        soloVencido: this.filtro.soloVencido,
        vencidoIncluyeAbonos: this.filtro.vencidoIncluyeAbonos,
        sinGestorAsignado: this.filtro.sinGestorAsignado
      };
      this.ruta.forEach(function(paso) {
        const dimension = DIMENSIONES[paso.dimension];
        if (dimension) {
          peticion[dimension.campo] = [paso.clave === null ? SIN_VALOR : paso.clave];
        }
      });
      return peticion;
    },
    /**
     * #749 · Si la lectura que se va a pedir recorta la cartera, y con qué palabras decirlo.
     *
     * Los filtros del panel se miran con los mismos campos que nombra `resumenFiltros`, para que la
     * cabecera de las cifras no diga «con los filtros puestos» mientras la de los filtros dice
     * «ninguno». El camino bajado también recorta —llega a la API como un filtro más, ver
     * `filtroParaLaApi`—, pero no sale en el resumen de los filtros sino en el del recorrido, así
     * que se nombra como lo que es.
     */
    recorteDeLaLectura: function() {
      const f = this.filtro;
      if (f.anioEmision || f.empleadoVentas || f.gestorCobro || f.cliente || f.estadoGestion
        || f.formaPago || f.tramo || f.soloVencido || f.sinGestorAsignado) {
        return 'con los filtros puestos';
      }
      return this.ruta.length > 0 ? 'con el recorrido bajado' : '';
    },
    aplicarFiltros: function() {
      // Cambiar un filtro invalida el camino: los niveles de abajo se calcularon con el conjunto
      // anterior y la miga apuntaría a un grupo que puede que ya no exista.
      this.ruta = [];
      this.cargar('FILTROS');
    },
    /**
     * Cambiar el año de emisión tira el mes.
     *
     * Sin esto el mes se queda puesto mientras su selector está deshabilitado y el resumen de
     * filtros no lo nombra —porque sin año no se nombra—, y al elegir otro año vuelve a aplicarse
     * solo: filtrar por marzo de 2026, quitar el año y poner 2025 consultaba marzo de 2025 sin que
     * nada en pantalla dijera que seguía habiendo un mes puesto. Un filtro que no se ve no puede
     * estar recortando la cartera.
     */
    cambiarAnioEmision: function() {
      this.filtro.mesEmision = null;
      this.aplicarFiltros();
    },
    limpiarFiltros: function() {
      this.filtro = filtrosEnBlanco();
      this.ruta = [];
      // Y también lo escrito en los tres buscadores: si no, dejan los desplegables acortados por un
      // texto que ya nadie relaciona con nada. «Quitar filtros» deja la pantalla como entró.
      this.busca = { empleadoVentas: '', gestorCobro: '', cliente: '' };
      this.cargar('FILTROS');
    },
    cambiarCriterioVencido: function(conAbonos) {
      this.filtro.vencidoIncluyeAbonos = conAbonos;
      this.aplicarFiltros();
    },
    /** Las opciones que manda el servidor para un filtro, con el resguardo en un solo sitio. */
    opcionesDe: function(campo) {
      return (this.dto && this.dto[campo]) || [];
    },
    /**
     * #749 · Las opciones de un desplegable que coinciden con lo escrito en su buscador.
     *
     * La búsqueda es la del resto del ERP (`buscarPersonas`), mirando el rótulo y la clave: así
     * «perez» encuentra a «Pérez», «martin lau» encuentra a «Laura Martín» y el código sirve de
     * atajo. Con el buscador vacío devuelve la lista entera.
     *
     * **La opción elegida nunca se esconde.** Si el buscador la dejara fuera, el `<select>` se
     * quedaría sin la opción de su propio valor: el navegador enseñaría otra —la primera— y la
     * pantalla estaría diciendo que filtra por un cliente que no es el que está filtrando. Se
     * devuelve delante y, si es la única, el desplegable sigue teniéndola.
     */
    opcionesBuscadas: function(campo, texto, elegida) {
      const opciones = this.opcionesDe(campo);
      const coinciden = buscarPersonas(opciones, texto, ['etiqueta', 'clave']);
      if (elegida === null || elegida === undefined || elegida === '') return coinciden;
      const estaDentro = coinciden.some(function(opcion) { return opcion.clave === elegida; });
      if (estaDentro) return coinciden;
      return opciones.filter(function(opcion) { return opcion.clave === elegida; })
        .concat(coinciden);
    },
    /**
     * Si se ha escrito algo en un buscador y no ha dejado nada que elegir.
     *
     * Hace falta decirlo: un desplegable que se queda con «Todos» y nada más, después de escribir,
     * no se distingue de uno que no tiene opciones, y la persona no sabe si el que busca no existe
     * o si el buscador no funciona. Con la opción elegida dentro la lista nunca está vacía, así
     * que esto solo sale cuando de verdad no coincide nada.
     */
    sinCoincidencias: function(cual, encontradas) {
      return !!this.busca[cual] && !encontradas.length;
    },
    /**
     * El rótulo de una opción, para el resumen de los filtros plegados. Si el servidor ya no la
     * manda —la cartera ha cambiado y ese valor no está en la lista— se escribe la clave tal cual:
     * un filtro puesto se nombra siempre, aunque sea con su código.
     */
    etiquetaDeOpcion: function(campo, clave) {
      const opciones = this.opcionesDe(campo);
      for (let i = 0; i < opciones.length; i++) {
        if (opciones[i].clave === clave) return opciones[i].etiqueta || String(clave);
      }
      return String(clave);
    },
    cambiarRecorrido: function(recorrido) {
      if (this.recorrido === recorrido) return;
      this.recorrido = recorrido;
      // El camino es el del recorrido anterior: sus niveles no son los de este.
      this.ruta = [];
      this.cargar('FILTROS');
    },
    /**
     * #830 · Un tramo sin documentos no baja aunque el gráfico sea el nivel actual: llevaría a un
     * nivel de empleados vacío. Su fila ya lo dice, con el 0 a la derecha.
     */
    tramoSePuedePinchar: function(tramo) {
      return this.tramoPinchable && (Number(tramo && tramo.documentos) || 0) > 0;
    },
    pincharTramo: function(tramo) {
      if (this.tramoSePuedePinchar(tramo)) this.bajarA('TRAMO', tramo.tramo, tramo.etiqueta);
    },
    /** Baja un nivel: añade el paso al camino y vuelve a pedir con ese filtro puesto. */
    bajarA: function(dimension, clave, etiqueta) {
      if (!DIMENSIONES[dimension]) return;
      this.ruta.push({ dimension: dimension, clave: clave === undefined ? null : clave, etiqueta: etiqueta });
      this.cargar('FILTROS');
    },
    /** Vuelve a un nivel de las migas. `0` es «Todo». */
    volverA: function(longitud) {
      if (longitud >= this.ruta.length) return;
      this.ruta = this.ruta.slice(0, longitud);
      this.cargar('FILTROS');
    },
    /** Las tareas de gestión de cobro de una factura, en el popup. */
    abrirGestiones: function(fila) {
      const self = this;
      self.gestiones.abierto = true;
      self.gestiones.error = null;
      self.gestiones.lista = [];
      self.gestiones.titulo = 'Gestiones de ' + (fila.codigo || 'la factura');
      self.carrilGestiones.lanzar(FactCobro.gestionesDeFactura(fila.id), {
        exito: function(respuesta) {
          self.gestiones.lista = (respuesta && respuesta.list) || [];
        },
        limpiar: function() {
          self.gestiones.lista = [];
        },
        fallo: function(e) {
          self.gestiones.error = e && e.status === 403
            ? 'Esa factura no está en la cartera que puedes ver.'
            : 'No se han podido cargar las gestiones de esta factura.';
        }
      });
    },
    /**
     * #201 · La lista de los documentos a los que afecta una fila de la calidad del dato.
     *
     * **Sirve a las siete filas del bloque**: las cinco de huecos y las dos de incoherencia
     * (`cobradoTotal` y `extinta`). Las tres cosas vienen con la misma forma —`campo` y
     * `etiqueta`— porque las calcula el mismo enum del servidor, y el endpoint las atiende todas.
     * Por eso el parámetro es una fila cualquiera y no un hueco: una alerta cuya lista se ha
     * recortado por el tope necesita exactamente esto para que sus documentos sean alcanzables.
     *
     * El campo que se manda es el que trae la propia fila (`fila.campo`) y no uno escrito aquí:
     * es la misma clave con la que el servidor contó esa fila, así que la lista y la cifra hablan
     * del mismo conjunto (criterio 3). Con el valor escrito en la pantalla, añadir una fila
     * obligaría a tocar los dos sitios y uno se quedaría atrás.
     *
     * No se le mandan los filtros de pantalla, y no es un olvido: el bloque se calcula sobre la
     * cartera visible entera, así que la lista tiene que hablar de ese mismo conjunto.
     */
    abrirDocumentosDeLaCalidad: function(fila) {
      const self = this;
      self.documentos.abierto = true;
      self.documentos.error = null;
      self.documentos.lista = [];
      self.documentos.titulo = fila.etiqueta || 'Documentos';
      self.carrilDocumentos.lanzar(FactCobro.documentosDeLaCalidad(fila.campo), {
        exito: function(respuesta) {
          self.documentos.lista = (respuesta && respuesta.list) || [];
        },
        limpiar: function() {
          self.documentos.lista = [];
        },
        fallo: function(e) {
          self.documentos.error = e && e.status === 403
            ? 'No se puede listar ese campo.'
            : 'No se ha podido cargar la lista de documentos de esta fila.';
        }
      });
    },
    /**
     * #201 · El listado ENTERO de los vínculos de gestión sin atribuir.
     *
     * Lo abre el pie de esa tabla cuando el tope la ha recortado, y existe por lo mismo que el
     * botón de las dos alertas: el pie dice cuántos vínculos no caben, y decirlo sin dar forma de
     * verlos deja inaccesible precisamente lo que este bloque viene a identificar.
     *
     * No pasa por `abrirDocumentosDeLaCalidad` aunque se parezca: estos vínculos no son documentos
     * de la cartera —son tareas que no han podido señalar a ninguno—, así que no tienen `campo` ni
     * las mismas columnas. Con un solo método habría que ramificar dentro, y el popup tendría que
     * decidir qué cabecera pinta según lo que le hayan metido en la lista.
     */
    abrirLosVinculosSinAtribuir: function() {
      const self = this;
      self.vinculos.abierto = true;
      self.vinculos.error = null;
      self.vinculos.lista = [];
      self.carrilVinculos.lanzar(FactCobro.gestionesSinAtribuirDeLaCalidad(), {
        exito: function(respuesta) {
          self.vinculos.lista = (respuesta && respuesta.list) || [];
        },
        limpiar: function() {
          self.vinculos.lista = [];
        },
        fallo: function() {
          self.vinculos.error = 'No se ha podido cargar el listado de gestiones sin atribuir.';
        }
      });
    },
    /**
     * #200 · La línea pequeña de debajo del nombre del escalón.
     *
     * Dos cosas distintas, y por eso no es una sola frase: si la fila tiene días medios, de cuántas
     * mediciones salen —una media de 14 días sacada de dos tramos no se lee igual que una sacada de
     * doscientos—; y si no los tiene, el motivo que manda la API, que es distinto según sea dato que
     * falta, dato sucio o una reclamación que todavía está en curso.
     *
     * Vacía en las filas a cero que tampoco han medido nada: ahí no hay nada que explicar y once
     * líneas de «no hay nada que medir» enseñan a no leer la tabla.
     */
    detalleEscalon: function(escalon) {
      if (escalon.diasMedios !== null && escalon.diasMedios !== undefined) {
        const tramos = escalon.tramosMedidos === 1 ? '1 tramo medido' : escalon.tramosMedidos + ' tramos medidos';
        const docs = escalon.documentosMedidos === 1 ? '1 documento' : escalon.documentosMedidos + ' documentos';
        return tramos + ' de ' + docs;
      }
      if (!escalon.documentos && !escalon.tramosSinMedida) return '';
      return escalon.faltaDiasMedios || '';
    },
    /**
     * El ancho de una parte de la barra, en porcentaje de la escala común.
     *
     * Valor absoluto: ver `escalaTramos`. Y `0%` cuando no hay importe, para que la parte no pinte
     * nada en vez de pintar un hilo que se lee como un dato.
     */
    ancho: function(importe) {
      const valor = Math.abs(Number(importe) || 0);
      if (!this.escalaTramos || !valor) return '0%';
      return (valor / this.escalaTramos * 100).toFixed(2) + '%';
    },
    /**
     * Una fecha del servidor (`2026-10-02`) como se escribe aquí: `02/10/2026`. Sin `new Date()`,
     * que interpreta la cadena en UTC y en Valladolid puede devolver el día de antes.
     */
    formatFecha: function(valor) {
      if (!valor) return '—';
      const partes = String(valor).substring(0, 10).split('-');
      if (partes.length !== 3) return String(valor);
      return partes[2] + '/' + partes[1] + '/' + partes[0];
    },
    /** Euros enteros, que es como se leen estas cifras. Un hueco es un guion, nunca un cero. */
    formatEuros: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Math.round(Number(valor)).toLocaleString('es-ES');
    },
    /**
     * #189 · Si esa factura ha cambiado de gestor tantas veces como el umbral o más, o sea si «se
     * está pasando de mano sin resolverse».
     *
     * El umbral lo manda el servidor (`personas.umbralCambiosDeGestor`) y no está escrito aquí: es
     * la misma cifra con la que cuenta la columna de la tabla por gestor, así que si estuviera en
     * los dos sitios el día que dirección la mueva una de las dos marcas se quedaría atrás y la
     * tabla de arriba contaría facturas que el detalle no marca.
     *
     * Sin umbral no se marca nada: es preferible no marcar a marcar con un número inventado.
     */
    seHaPasadoDeMano: function(fila) {
      const umbral = this.personas.umbralCambiosDeGestor;
      if (!umbral) return false;
      return Number(fila.cambiosDeGestor) >= umbral;
    },
    /**
     * Un importe con su unidad, o un guion **sin unidad** cuando no hay dato.
     *
     * Hace falta donde el hueco es lo normal —las filas de gestor sin nada asignado, que son hoy
     * todas—: con la unidad escrita en la plantilla, un nulo se pintaba como «— €», que se lee como
     * que hay una cifra de cero euros y es justo la confusión que el guion viene a evitar
     * (D-COB-8).
     */
    formatEurosConUnidad: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return this.formatEuros(valor) + ' €';
    },
    formatPorcentaje: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Number(valor).toFixed(1).replace('.', ',') + ' %';
    },
    formatDias: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Math.round(Number(valor)).toLocaleString('es-ES') + ' días';
    },
    /**
     * Un número de columna, con el hueco como guion.
     *
     * D-COB-8 en una línea: un documento sin fecha de vencimiento no tiene «0 días de retraso»,
     * tiene «no se sabe», y un cero ahí se lee como que está al día.
     */
    numero: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Number(valor).toLocaleString('es-ES');
    },
    /** Un texto de columna, con el hueco como guion. Mismo motivo que `numero`. */
    texto: function(valor) {
      return valor === null || valor === undefined || valor === '' ? '—' : String(valor);
    }
  }
};
</script>
